import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ShimmerLoader from './ShimmerLoader';
import FileUpload from './FileUpload';
import EmailImportButton from './EmailImportButton';
import { directQuery, directUpdate } from '../utils/directFetch';
import supabase from '../config/supabase';
import { SUPABASE_URL, getSupabaseHeaders } from '../config/database';
import {
  assignVendorToItem,
  clearVendorDirectoryCache,
  createVendorAliasIfNeeded,
  normalizeVendorName
} from '../utils/vendorResolution';
import { recordPriceHistory } from '../utils/priceHistory';
import TemplateOfferPrompt from './TemplateOfferPrompt';
import ItemPriceTimeline from './ItemPriceTimeline';
import PurchaseOptionsEditor from './PurchaseOptionsEditor';
import CrossReferencesTab from './CrossReferencesTab';
import {
  countPendingSuggestions,
  createOptionForItem,
  resolveInventoryInsertResults,
} from '../services/itemCrossReferenceService';
import { fetchEquivalentGroupComparisons, fetchRelationshipsForStore } from '../services/itemRelationshipService';
import { fetchComparisonItems, fetchLastOrderUseByOption, fetchOptionsForItems } from '../services/purchaseOptionService';
import { fetchAliasMap, matchAliases } from '../services/itemAliasService';
import { fetchAiMatchesForItems, fetchAiMatchesForLines, mergeAiCandidates } from '../services/aiItemMatchService';
import { findCrossReferenceCandidates } from '../utils/itemCrossReference';
import LinkItemDialog from './LinkItemDialog';
import ItemCostSummaryCell from './ItemCostSummaryCell';
import RowActionMenu from './RowActionMenu';
import CanonicalLinkEditor from './CanonicalLinkEditor';
import ItemPurchaseSummary from './ItemPurchaseSummary';
import ManageRelationshipsView from './ManageRelationshipsView';
import RelatedProductGroupsView from './RelatedProductGroupsView';
import RelatedProductGroupEditor from './RelatedProductGroupEditor';
import {
  buildRelatedProductCatalogGroups,
  foldCatalogGroupsByEquivalence,
} from '../utils/relatedProductCatalog';
import {
  buildComparablePriceDisplay,
  comparablePrecision,
  formatComparablePrice,
  roundHalfUpFixed,
} from '../utils/priceComparison';
import { buildGroupSavings, formatSavingsLine } from '../utils/groupSavings';
import { lastUsedTitle, resolveLastUsedOption } from '../utils/lastUsedOption';
import ImportEnrichmentStatusBanner from './ImportEnrichmentStatusBanner';
import { HelpInfoButton } from './HelpTooltip';
import GroupMemberNamesTooltip from './GroupMemberNamesTooltip';
import { buildItemCostSummary, formatUnitPrice, describePurchaseOptions } from '../utils/itemCostSummary';
import { buildWorkingSetGroups, expandOptionsAcrossGroup, groupMembersFor } from '../utils/relatedItemGroups';
import { foldRowsToCanonicalMembers, secondaryRelationshipMetrics } from '../utils/relatedProductCatalog';
import { buildRawInventoryCsvRows, RAW_INVENTORY_CSV_COLUMNS } from '../utils/inventoryCsv';

// Wrapper to mount the vanilla-JS MenuStarterUI inside a React tree
const MenuStarterMount = ({ currentStore, onCompleted }) => {
  const containerRef = useRef(null);
  const controllerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 30;

    const tryMount = () => {
      if (cancelled || !containerRef.current) return;
      if (!window.MenuStarterUI || !window.MenuStarterWorkflow) {
        attempts += 1;
        if (attempts >= maxAttempts) {
          containerRef.current.innerHTML = '<div class="alert alert-warning">Menu starter scripts are not loaded. Please refresh the page.</div>';
          return;
        }
        window.setTimeout(tryMount, 150);
        return;
      }

      // Get current user email
      let userEmail = null;
      try { userEmail = JSON.parse(localStorage.getItem('currentUser') || '{}')?.email || null; } catch (_) {}

      const prefillWebsite = currentStore?.service_metadata?.restaurant_website_url || '';

      controllerRef.current = window.MenuStarterUI.mount(containerRef.current, {
        storeId: currentStore?.id || null,
        userEmail,
        source: 'items_page',
        prefillWebsite,
        // Existing users opt into templates via a prompt instead of the setup
        // flow's silent auto-create + redirect to Counts. The host receives the
        // approved item IDs and decides whether to build templates.
        deferTemplateCreation: true,
        onCompleted: (workflow, info) => {
          if (typeof onCompleted === 'function') onCompleted(workflow, info);
        },
      });
    };

    tryMount();

    return () => {
      cancelled = true;
      if (controllerRef.current?.destroy) controllerRef.current.destroy();
      controllerRef.current = null;
    };
  }, [currentStore?.id]);

  return <div ref={containerRef} style={{ minHeight: 200 }} />;
};

const hasField = (item, key) => Object.prototype.hasOwnProperty.call(item || {}, key);

const normalizeText = (value) => (value ?? '').toString().trim();

const toNullableText = (value) => {
  const normalized = normalizeText(value);
  return normalized ? normalized : null;
};

const toNullableInt = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) return null;
  const parsed = Number.parseInt(normalized, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const toNullableFloat = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const buildEditorDraft = (item) => ({
  description: item?.description || '',
  brand: item?.brand || '',
  category: item?.category || '',
  location: item?.location || '',
  vendor: item?.vendor || '',
  pack_size: item?.pack_size || '',
  unit: item?.unit || '',
  case_qty: item?.case_qty ?? item?.case_quantity ?? '',
  split_qty: item?.split_qty ?? item?.split_quantity ?? '',
  case_price: item?.case_price ?? '',
  unit_price: item?.each_price ?? item?.unit_price ?? '',
  item_code: item?.supc ?? item?.manufacturer_code ?? '',
  // Tri-state sales-tax flag as select values: '' = unknown, 'true' = taxed,
  // 'false' = not taxed. Undefined (column not loaded) reads as unknown.
  taxable: item?.taxable === true ? 'true' : item?.taxable === false ? 'false' : ''
});

const INVENTORY_BASE_COLUMNS =
  'id,store_id,created_at,updated_at,description,brand,category,location,vendor,pack_size,unit,'
  + 'case_quantity,split_quantity,case_price,unit_price,manufacturer_code,strict_specification';

// Cost columns behind the item list's "Current inventory cost" slot. Split out
// so a database missing the later migrations can still load the catalog.
const INVENTORY_COST_COLUMNS =
  'verified_unit_cost,verified_cost_source,verified_cost_effective_date,'
  + 'verified_cost_purchase_option_id,costing_method,standard_unit_cost';

// Taxability columns ship in migration 20260816120000 (invoice additional
// charges). Split out like the cost columns so a database that has not caught
// up still loads the catalog — the tri-state select just starts blank.
const INVENTORY_TAX_COLUMNS = ['taxable', 'taxable_source'].join(',');

// Vendor-independent fields the phone card lets you edit inline. Vendor,
// package price, and unit price are deliberately absent: on a multi-vendor
// item they have no single answer, and they live on the vendor products.
const MOBILE_EDIT_FIELDS = [
  { key: 'description', label: 'Name' },
  { key: 'category', label: 'Category' },
  {
    key: 'taxable',
    label: 'Sales tax',
    type: 'select',
    options: [
      { value: '', label: '—' },
      { value: 'true', label: 'Taxed' },
      { value: 'false', label: 'Not taxed' },
    ],
  },
  { key: 'location', label: 'Area' },
  { key: 'brand', label: 'Brand' },
  { key: 'unit', label: 'Inventory unit' },
  { key: 'pack_size', label: 'Pack / size' },
  { key: 'item_code', label: 'Code' },
];

const SORTABLE_COLUMNS = [
  { key: 'description', label: 'Name' },
  { key: 'vendor', label: 'Primary vendor' },
  { key: 'category', label: 'Category' },
  { key: 'taxable', label: 'Taxed' },
  { key: 'created_at', label: 'Date added' },
  { key: 'location', label: 'Area' },
  { key: 'brand', label: 'Brand' },
  { key: 'pack_size', label: 'Pack/Size' },
  { key: 'unit', label: 'Unit' },
  { key: 'case_qty', label: 'Case Qty' },
  { key: 'split_qty', label: 'Split Qty' },
  { key: 'case_price', label: 'Case $' },
  { key: 'unit_price', label: 'Unit $' },
  { key: 'item_code', label: 'Code' },
];
const SORTABLE_FIELDS = SORTABLE_COLUMNS.map(({ key }) => key);
const NUMERIC_SORT_FIELDS = new Set(['case_qty', 'split_qty', 'case_price', 'unit_price']);
const SEARCHABLE_FIELDS = ['description', 'vendor', 'category', 'location', 'brand', 'item_code'];

// The catalog used to fetch every row before it rendered anything, which is why
// phones were opted out of loading it at all — a 2,000-item store meant ten
// sequential round trips before first paint, and then 2,000 rows of DOM.
//
// Now the first page paints on its own and the rest streams in behind it:
//   FIRST_PAGE_ROWS  — what the user waits for. One request, then content.
//   HYDRATION_PAGE_ROWS — the background pages that follow, larger because
//                    nobody is watching them.
//   RENDER_WINDOW_*  — how many rows are ever in the DOM at once. This, not the
//                    size of the JS array, is what actually costs a phone
//                    memory; 3,000 rows of inputs will kill a browser long
//                    before 3,000 objects do.
const FIRST_PAGE_ROWS = 100;
const HYDRATION_PAGE_ROWS = 500;
const RENDER_WINDOW_MOBILE = 40;
const RENDER_WINDOW_DESKTOP = 200;

// Columns the server-side search covers. Kept in step with SEARCHABLE_FIELDS:
// `item_code` is the client-side name for the `manufacturer_code` column, and
// `location` was missing entirely, so searching by area found nothing on a
// phone even though the placeholder promised it.
const REMOTE_SEARCH_COLUMNS = ['description', 'vendor', 'category', 'location', 'brand', 'manufacturer_code'];

// PostgREST parses `or=(...)` as a comma-separated list, so a comma, parenthesis
// or backslash in the needle rewrites the filter rather than being matched by
// it — searching `olive oil, extra virgin` used to 400 and surface as "no
// results". Wrapping the pattern in double quotes and escaping the quote and
// backslash keeps the whole needle inside one operand. `%` and `_` are left
// alone deliberately: they are LIKE wildcards and a user typing one means it.
const escapeOrPattern = (needle) => `"%${needle.replace(/(["\\])/g, '\\$1')}%"`;

const sortableValue = (row, field) => {
  const rawValue = row?.[field];

  if (field === 'created_at') {
    const timestamp = rawValue ? new Date(rawValue).getTime() : Number.NaN;
    return Number.isFinite(timestamp) ? { blank: false, value: timestamp } : { blank: true, value: 0 };
  }

  if (NUMERIC_SORT_FIELDS.has(field)) {
    const value = rawValue === '' || rawValue === null || rawValue === undefined
      ? Number.NaN
      : Number(rawValue);
    return Number.isFinite(value) ? { blank: false, value } : { blank: true, value: 0 };
  }

  if (field === 'taxable') {
    if (rawValue === true || rawValue === 'true') return { blank: false, value: 1 };
    if (rawValue === false || rawValue === 'false') return { blank: false, value: 0 };
    return { blank: true, value: 0 };
  }

  const value = normalizeText(rawValue);
  return value ? { blank: false, value } : { blank: true, value: '' };
};

const compareInventoryRows = (left, right, field, direction) => {
  const leftSort = sortableValue(left, field);
  const rightSort = sortableValue(right, field);

  // Keep empty cells at the bottom in either direction, matching the useful
  // part of spreadsheet sorting instead of lifting incomplete rows to the top.
  if (leftSort.blank !== rightSort.blank) return leftSort.blank ? 1 : -1;
  if (leftSort.blank) return 0;

  const comparison = typeof leftSort.value === 'number'
    ? leftSort.value - rightSort.value
    : leftSort.value.localeCompare(rightSort.value, undefined, {
        sensitivity: 'base',
        numeric: true,
      });

  return direction === 'asc' ? comparison : -comparison;
};

const FinderSortableHeader = ({ field, label, sortField, sortDirection, onSort, className = '', children = null }) => {
  const isActive = sortField === field;
  const nextDirection = isActive && sortDirection === 'asc' ? 'descending' : 'ascending';

  return (
    <th
      scope="col"
      className={className || undefined}
      aria-sort={isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <div className="finder-sort-header">
        <button
          type="button"
          className={`finder-sort-button${isActive ? ' active' : ''}`}
          data-sort-field={field}
          onClick={() => onSort(field)}
          title={`Sort by ${label} (${nextDirection})`}
          aria-label={`Sort by ${label} ${nextDirection}`}
        >
          <span>{label}</span>
          <span className="finder-sort-indicator" aria-hidden="true">
            {isActive ? (sortDirection === 'asc' ? '\u25b2' : '\u25bc') : '\u2195'}
          </span>
        </button>
        {children}
      </div>
    </th>
  );
};

const formatDateAdded = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString();
};

const formatHistoryTimestamp = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString();
};

const formatPrice = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(num)) return '—';
  return `$${num.toFixed(2)}`;
};

const formatDeltaAbsolute = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(num)) return '—';
  const sign = num > 0 ? '+' : num < 0 ? '−' : '';
  return `${sign}$${Math.abs(num).toFixed(2)}`;
};

const formatDeltaPct = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(num)) return '—';
  const sign = num > 0 ? '+' : num < 0 ? '−' : '';
  return `${sign}${Math.abs(num).toFixed(1)}%`;
};

const deltaColor = (value) => {
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(num) || num === 0) return '#6f6259';
  return num > 0 ? '#b3261e' : '#1f7a3f';
};

const SOURCE_LABELS = {
  ai_price_import: 'AI Invoice Import',
  email_invoice_import: 'Emailed Invoice',
  invoice_approval: 'Invoice Approved',
  item_editor: 'Item Editor',
  web_count: 'Count Session (Web)',
  mobile_count: 'Count Session (Mobile)',
  import_approval: 'Import Approval',
};

const formatSource = (value) => SOURCE_LABELS[value] || value || '—';

const csvEscape = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const numberForCsv = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(num)) return '';
  return num.toFixed(2);
};

const isoDateForCsv = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString();
};

const slugForFilename = (value) => {
  if (!value) return 'item';
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'item';
};

const normalizeRow = (item, draft = null) => {
  const base = buildEditorDraft(item);
  const merged = draft ? { ...base, ...draft } : base;
  return {
    id: item.id,
    created_at: item.created_at || '',
    ...merged
  };
};

const dedupeInventoryRowsById = (rows) => {
  const byId = new Map();
  (rows || []).forEach((row) => {
    if (!row || row.id == null) return;
    const current = byId.get(row.id);
    if (!current) {
      byId.set(row.id, row);
      return;
    }
    const currentUpdated = current.updated_at ? new Date(current.updated_at).getTime() : 0;
    const nextUpdated = row.updated_at ? new Date(row.updated_at).getTime() : 0;
    if (nextUpdated >= currentUpdated) {
      byId.set(row.id, row);
    }
  });
  return [...byId.values()];
};

const MANUAL_ENTRY_COLUMNS = [
  { key: 'description', label: 'Inventory item name', required: true, placeholder: 'e.g. Olive Oil' },
  { key: 'category',    label: 'Category', required: true, placeholder: 'e.g. Dry Goods' },
  { key: 'unit',        label: 'Unit', required: true, placeholder: 'each / case / lbs' },
  { key: 'location',   label: 'Location', required: true, placeholder: 'e.g. Dry Storage' },
  { key: 'brand',      label: 'Brand', required: false, placeholder: 'e.g. Sysco' },
  { key: 'vendor',     label: 'Vendor', required: false, placeholder: 'e.g. Sysco' },
  { key: 'case_price', label: 'Case Price', required: false, placeholder: '0.00', numeric: true },
  { key: 'unit_price', label: 'Unit Price', required: false, placeholder: '0.00', numeric: true },
];

// inventory_items.user_defined_name / created_source ship in migration
// 20260724140001, which can lag the UI deploy — PostgREST flags an unknown
// column as PGRST204 (schema cache) and Postgres as 42703.
const isMissingColumnError = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return (
    ['PGRST204', '42703'].includes(code) ||
    /column .* does not exist|could not find the .* column|schema cache/i.test(message)
  );
};

// Insert inventory_items rows including the identity columns
// (user_defined_name / created_source); on a missing-column error, retry
// without them so the save still works where the migration isn't deployed.
const insertInventoryItems = async (items, selectColumns) => {
  const first = await supabase.from('inventory_items').insert(items).select(selectColumns);
  if (!first.error || !isMissingColumnError(first.error)) return first;
  const stripped = items.map(({ user_defined_name, created_source, ...rest }) => rest);
  return supabase.from('inventory_items').insert(stripped).select(selectColumns);
};

function makeEmptyRows(n) {
  return Array.from({ length: n }, () =>
    Object.fromEntries(MANUAL_ENTRY_COLUMNS.map(c => [c.key, '']))
  );
}

const InventoryManager = () => {
  const [entryMode, setEntryMode] = useState('edit');
  const [isImportToolModalOpen, setIsImportToolModalOpen] = useState(false);
  const [inventories, setInventories] = useState([]);
  const [loading, setLoading] = useState(false);
  // False until the first catalog read settles. `loading` starts false and is
  // not flipped until the store has been resolved from localStorage, so on its
  // own it let the list paint "Your catalog is empty. Add items above" at every
  // mount — a claim about the store, made before the store had been asked.
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const [currentStore, setCurrentStore] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [finderQuery, setFinderQuery] = useState('');
  const [finderVendor, setFinderVendor] = useState('all');
  const [finderCategory, setFinderCategory] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [inlineEdits, setInlineEdits] = useState({});
  const [dirtyItemIds, setDirtyItemIds] = useState([]);
  const [savingItem, setSavingItem] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  // Bulk delete (soft delete / archive): checkbox selection + two-step confirm.
  const [selectedItemIds, setSelectedItemIds] = useState(() => new Set());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteStage, setDeleteStage] = useState('confirm'); // 'confirm' → 'type'
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  ));
  // Progress of the background catalog fetch. `complete` is the important one:
  // while it is false the client only holds part of the store, so a search has
  // to ask the server as well as filter what is already here.
  const [hydration, setHydration] = useState({ loaded: 0, total: null, complete: false, failed: false });
  // How many of the matching rows are actually rendered. Reset whenever the
  // query or filters change so a new search always starts at the top.
  const [renderLimit, setRenderLimit] = useState(RENDER_WINDOW_DESKTOP);
  const [aliasPrompt, setAliasPrompt] = useState(null);
  // Item IDs for the opt-in "create templates from these items?" prompt shown
  // after an import completes (modal itself lives in TemplateOfferPrompt,
  // which hands the batch to CounterIntelligence AI when the user accepts).
  const [templateOfferIds, setTemplateOfferIds] = useState(null);
  const importModalOverlayRef = useRef(null);
  const mouseDownOnOverlayRef = useRef(false);
  const [historyItem, setHistoryItem] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  // Purchase options for every listed item, keyed by inventory_item_id, plus
  // the store's vendor display names and costing setting. Together these turn
  // each row from "a vendor and a price" into the four labelled money concepts
  // (see utils/itemCostSummary). All three loads are non-fatal: the catalog
  // stays fully editable when purchasing data is unavailable.
  const [optionsByItemId, setOptionsByItemId] = useState({});
  const [comparisonItemById, setComparisonItemById] = useState({});
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState(false);
  const [vendorNameById, setVendorNameById] = useState({});
  const [storeSettings, setStoreSettings] = useState(null);
  const [expandedCostItemId, setExpandedCostItemId] = useState(null);
  const [catalogGroupModalKey, setCatalogGroupModalKey] = useState(null);

  // Item whose purchase options (ways to buy) are open in the per-item modal.
  const [purchaseOptionsItem, setPurchaseOptionsItem] = useState(null);
  const [vendorMatchingOpen, setVendorMatchingOpen] = useState(false);
  const [pendingVendorMatchCount, setPendingVendorMatchCount] = useState(0);
  const [catalogView, setCatalogView] = useState('items');
  // When true (default) the item list folds confirmed EQUIVALENT groups into one
  // row per group — the same related-product grouping the Related Products tab
  // shows, with the pill stating how many vendors/products it speaks for.
  // When false the catalog is flat: every inventory item renders independently
  // and the related-product pill is suppressed.
  const [catalogGrouped, setCatalogGrouped] = useState(true);
  // Within the Related Products tab: 'products' is the catalog's own meaning of
  // related — one item, several vendor products. 'substitutes' is the separate
  // cross-item alternatives graph, which is a different question and no longer
  // the thing this tab opens on.
  const [relatedSubView, setRelatedSubView] = useState('products');
  const [trendsPanelOpen, setTrendsPanelOpen] = useState(false);
  const [trendsWindow, setTrendsWindow] = useState(30);
  const [trendsVendor, setTrendsVendor] = useState('all');
  const [trendsCategory, setTrendsCategory] = useState('all');
  const [trendsDirection, setTrendsDirection] = useState('all');
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsError, setTrendsError] = useState(null);
  const [trendsRawRows, setTrendsRawRows] = useState([]);
  const [trendsLoadedAt, setTrendsLoadedAt] = useState(null);
  const [isManualEntryOpen, setIsManualEntryOpen] = useState(false);
  const [manualRows, setManualRows] = useState(() => makeEmptyRows(5));
  const [manualSaving, setManualSaving] = useState(false);
  const [manualSaveError, setManualSaveError] = useState(null);
  const [manualPriceErrorCells, setManualPriceErrorCells] = useState(null);
  // Item aliases ("whl milk" → Whole Milk) for alias-aware search — one fetch
  // per store, non-fatal (the item_aliases migration may not be deployed yet).
  const [itemAliasMap, setItemAliasMap] = useState({});
  // Sequential cross-reference review queue for manual entry (LinkItemDialog):
  // { contexts, queueIdxs, cursor, resolutions, enrichedCandidates } | null.
  const [linkReview, setLinkReview] = useState(null);
  const [stats, setStats] = useState({
    totalItems: 0,
    categories: 0,
    areas: 0,
    vendors: 0
  });
  const deferredFinderQuery = useDeferredValue(finderQuery);

  // Phones used to be skipped here entirely, which is why the catalog "did not
  // load on mobile": it was never asked to. It loads on every viewport now —
  // the first page is one request, and the render window keeps the DOM small.
  useEffect(() => {
    loadStoreAndFetchInventories();
  }, []);

  // The render window follows the viewport, but only as a starting point — a
  // "Show more" the user already pressed is not undone by rotating the phone.
  useEffect(() => {
    setRenderLimit((current) => Math.max(
      current,
      isMobileViewport ? RENDER_WINDOW_MOBILE : RENDER_WINDOW_DESKTOP
    ));
  }, [isMobileViewport]);

  // The setup-style "Add items" panel (public/js/items-add-experience.js) lives
  // outside React on items.html. After it imports or saves items it dispatches
  // this event so the catalog re-fetches in place instead of forcing a reload.
  useEffect(() => {
    const handleCatalogUpdated = (event) => {
      const storeId = (event && event.detail && event.detail.storeId)
        || currentStore?.id
        || (() => { try { return JSON.parse(localStorage.getItem('currentStore') || 'null')?.id; } catch (_) { return null; } })();
      if (storeId) {
        fetchInventories(storeId);
        refreshPendingVendorMatchCount(storeId);
        // A suggestion only stands while both of its items are live, so any
        // change to the catalog can change the badge. It used to be read once
        // per store and never again, which is how "141 waiting" survived a
        // delete-everything while the panel below it correctly said there were
        // none — the list re-queried, the number did not.
        // The relationship graph is the same story: an import writes rows into
        // it, and without this the Relationships tab kept showing the graph as
        // it stood when the page loaded.
        refreshItemRelationships(storeId);
      }
    };
    document.addEventListener('items:catalog-updated', handleCatalogUpdated);
    return () => document.removeEventListener('items:catalog-updated', handleCatalogUpdated);
  }, [currentStore?.id]);

  // Counter Intelligence (the chat drawer) changes the catalog through other
  // paths — archiving items from a review card, bulk edits, single-item creates,
  // price changes/updates, and chat imports — and dispatches
  // `counter-intelligence:applied` on window after each apply. Refetch on the
  // catalog-changing kinds so the table reflects them without a manual refresh.
  // (Template/order/PAR kinds are dispatched too but don't change catalog rows.)
  useEffect(() => {
    const CATALOG_KINDS = new Set(['item_dedup', 'item_edit', 'item_create', 'price_change', 'price_update', 'item_import']);
    const handleAiApplied = (event) => {
      const kind = event && event.detail && event.detail.kind;
      if (!CATALOG_KINDS.has(kind)) return;
      const storeId = (event && event.detail && event.detail.storeId)
        || currentStore?.id
        || (() => { try { return JSON.parse(localStorage.getItem('currentStore') || 'null')?.id; } catch (_) { return null; } })();
      if (storeId) {
        fetchInventories(storeId);
        refreshPendingVendorMatchCount(storeId);
        refreshItemRelationships(storeId);
      }
    };
    window.addEventListener('counter-intelligence:applied', handleAiApplied);
    return () => window.removeEventListener('counter-intelligence:applied', handleAiApplied);
  }, [currentStore?.id]);

  // The Add Items experience (items-add-experience.js) saves imports itself
  // and hands the new item IDs over so this component can show its opt-in
  // "Create templates from these items?" prompt. Saying yes opens the same
  // CounterIntelligence conversation the setup wizard runs.
  useEffect(() => {
    const handleOfferTemplates = (event) => {
      const ids = Array.isArray(event?.detail?.itemIds) ? event.detail.itemIds : [];
      if (ids.length > 0) offerTemplateCreation(ids);
    };
    document.addEventListener('items:offer-templates', handleOfferTemplates);
    return () => document.removeEventListener('items:offer-templates', handleOfferTemplates);
  }, []);

  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem('currentUser') || '{}');
      setCurrentUserId(parsed?.id || null);
    } catch (_) {
      setCurrentUserId(null);
    }
  }, []);

  // Load the store's item aliases once per store so the finder search can also
  // match a vendor's wording ('whl milk' → Whole Milk). Non-fatal.
  useEffect(() => {
    if (!currentStore?.id) return undefined;
    let cancelled = false;
    fetchAliasMap(currentStore.id)
      .then((map) => { if (!cancelled) setItemAliasMap(map || {}); })
      .catch((err) => { console.warn('⚠️ [INVENTORY-MANAGER] Item aliases unavailable:', err?.message || err); });
    return () => { cancelled = true; };
  }, [currentStore?.id]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleViewportChange = (event) => setIsMobileViewport(event.matches);
    setIsMobileViewport(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleViewportChange);
      return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }
    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  // ── Remote search ─────────────────────────────────────────────────────
  // The client only holds part of the store until hydration finishes, so a
  // query has to be put to the server as well as to the rows already here.
  // Results are merged with the local matches and deduped by id, so a row that
  // is both loaded and returned by the server appears exactly once.
  //
  // This used to be mobile-only AND gated on the catalog being completely
  // empty, which meant it stopped working the moment anything populated
  // `inventories` — including the very import the user was trying to confirm.
  // It now runs on any viewport, for exactly as long as the local copy is
  // incomplete.
  const [remoteSearchResults, setRemoteSearchResults] = useState([]);
  const [remoteSearching, setRemoteSearching] = useState(false);
  const remoteSearchTimerRef = useRef(null);
  const remoteSearchVersionRef = useRef(0);

  useEffect(() => {
    const trimmed = deferredFinderQuery.trim();
    // Once the whole store is local, the client-side filter is authoritative
    // and strictly better: it searches vendor products and aliases too.
    if (hydration.complete || trimmed.length < 2) {
      setRemoteSearchResults([]);
      setRemoteSearching(false);
      return undefined;
    }
    if (remoteSearchTimerRef.current) clearTimeout(remoteSearchTimerRef.current);
    setRemoteSearching(true);
    const version = ++remoteSearchVersionRef.current;
    remoteSearchTimerRef.current = setTimeout(async () => {
      try {
        const storedStore = (() => {
          try { return JSON.parse(localStorage.getItem('currentStore') || 'null'); } catch (_) { return null; }
        })();
        const storeId = currentStore?.id || storedStore?.id;
        if (!storeId) { setRemoteSearching(false); return; }
        const pattern = escapeOrPattern(trimmed);
        const { data, error } = await supabase
          .from('inventory_items')
          .select(`${INVENTORY_BASE_COLUMNS},${INVENTORY_TAX_COLUMNS}`)
          .eq('store_id', storeId)
          .not('is_active', 'is', false)
          .or(REMOTE_SEARCH_COLUMNS.map((column) => `${column}.ilike.${pattern}`).join(','))
          .limit(100);
        if (version !== remoteSearchVersionRef.current) return;
        if (error) {
          // A missing tax column is the one failure worth retrying — the same
          // ladder the catalog load walks.
          if (isMissingColumnError(error)) {
            const retry = await supabase
              .from('inventory_items')
              .select(INVENTORY_BASE_COLUMNS)
              .eq('store_id', storeId)
              .not('is_active', 'is', false)
              .or(REMOTE_SEARCH_COLUMNS.map((column) => `${column}.ilike.${pattern}`).join(','))
              .limit(100);
            if (version !== remoteSearchVersionRef.current) return;
            setRemoteSearchResults(retry.error ? [] : (retry.data || []));
            return;
          }
          console.warn('⚠️ [INVENTORY-MANAGER] Catalog search failed:', error?.message || error);
          setRemoteSearchResults([]);
          return;
        }
        setRemoteSearchResults(data || []);
      } catch (err) {
        if (version !== remoteSearchVersionRef.current) return;
        console.warn('⚠️ [INVENTORY-MANAGER] Catalog search failed:', err?.message || err);
        setRemoteSearchResults([]);
      } finally {
        if (version === remoteSearchVersionRef.current) setRemoteSearching(false);
      }
    }, 300);
    return () => { if (remoteSearchTimerRef.current) clearTimeout(remoteSearchTimerRef.current); };
  }, [deferredFinderQuery, hydration.complete, currentStore?.id]);

  // Vendor options for rows that arrived by remote search rather than by the
  // catalog load — otherwise a searched-for item shows a card with no price,
  // which is the one number the search was run to find.
  useEffect(() => {
    if (remoteSearchResults.length === 0) return;
    const storeId = currentStore?.id;
    if (!storeId) return;
    const unknown = remoteSearchResults.filter((row) => !(row.id in optionsByItemId));
    if (unknown.length === 0) return;
    // Only the rows whose options are genuinely unknown, appended — re-sending
    // the whole catalog on every keystroke would be a sweep per search.
    loadPurchaseOptions(storeId, unknown, { append: true });
    // `optionsByItemId` is intentionally not a dependency: it is what this
    // effect writes, and depending on it would re-run the load on its own result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteSearchResults, currentStore?.id]);

  useEffect(() => {
    if (!isImportToolModalOpen) return undefined;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [isImportToolModalOpen]);

  const loadStoreAndFetchInventories = async () => {
    try {
      const storedStore = localStorage.getItem('currentStore');
      if (storedStore) {
        const parsedStore = JSON.parse(storedStore);
        setCurrentStore(parsedStore);
        await fetchInventories(parsedStore.id);
        return;
      }

      setTimeout(() => {
        const delayedStore = JSON.parse(localStorage.getItem('currentStore') || 'null');
        if (!delayedStore) {
          setInventories([]);
          updateStats([]);
          setFirstLoadDone(true);
          return;
        }

        setCurrentStore(delayedStore);
        fetchInventories(delayedStore.id);
      }, 1000);
    } catch (error) {
      console.error('❌ [INVENTORY-MANAGER] Error loading store:', error);
      setInventories([]);
      updateStats([]);
      setFirstLoadDone(true);
    }
  };

  // Which column groups this database actually has. Resolved once per load by
  // the first page, then reused by every background page — without this the
  // fallback ladder would be re-walked on all of them.
  const selectClauseRef = useRef(null);
  // Bumped on every fresh load so a background hydration belonging to a
  // previous store (or a previous refresh) cannot append its rows into the
  // current one.
  const hydrationRunRef = useRef(0);

  const fetchInventoryPage = useCallback(async (storeId, { limit, offset }) => {
    const queryWith = (select) => directQuery('inventory_items', {
      select,
      eq: { store_id: storeId },
      // Hide soft-deleted/archived items (is_active=false) so deleted items drop
      // out of the catalog; active rows (is_active true or NULL) all stay.
      not_is: { is_active: false },
      // Paging is only stable under a total order. `created_at` alone ties on
      // bulk imports — every row of a paste shares a timestamp — and a tie lets
      // the same row arrive on two pages while another never arrives at all.
      // `id` breaks the tie.
      order: { column: 'created_at', ascending: false },
      order2: { column: 'id', ascending: false },
      limit,
      offset,
    });

    // The cost columns feed the item list's "Current inventory cost" and its
    // costing-method label, and the tax columns feed the taxed select. They
    // arrive with later migrations, so a database that has not caught up
    // falls back column-group by column-group rather than failing the whole
    // catalog load.
    if (selectClauseRef.current) {
      return queryWith(selectClauseRef.current);
    }

    const ladder = [
      `${INVENTORY_BASE_COLUMNS},${INVENTORY_COST_COLUMNS},${INVENTORY_TAX_COLUMNS}`,
      `${INVENTORY_BASE_COLUMNS},${INVENTORY_COST_COLUMNS}`,
      INVENTORY_BASE_COLUMNS,
    ];
    let result = { data: null, error: null };
    for (let rung = 0; rung < ladder.length; rung += 1) {
      // eslint-disable-next-line no-await-in-loop
      result = await queryWith(ladder[rung]);
      if (!result.error) {
        // Remember the rung that worked so the background pages skip the ladder.
        selectClauseRef.current = ladder[rung];
        break;
      }
      if (!isMissingColumnError(result.error)) break;
      console.warn(`[INVENTORY-MANAGER] Column group unavailable; retrying with a narrower select (rung ${rung + 1}).`);
    }
    return result;
  }, []);

  const fetchInventories = async (storeId) => {
    if (!storeId) return;

    const run = ++hydrationRunRef.current;
    selectClauseRef.current = null;
    setLoading(true);
    setHydration({ loaded: 0, total: null, complete: false, failed: false });
    try {
      // ── First page ────────────────────────────────────────────────────
      // This is the only fetch the user waits on. Everything after it lands
      // underneath rows that are already on screen.
      const { data, error } = await fetchInventoryPage(storeId, { limit: FIRST_PAGE_ROWS, offset: 0 });

      if (error) {
        console.error('❌ [INVENTORY-MANAGER] Direct fetch error:', error);
        setInventories([]);
        updateStats([]);
        setHydration({ loaded: 0, total: 0, complete: true, failed: true });
        return;
      }

      const inventoryData = dedupeInventoryRowsById(data || []);
      if (run !== hydrationRunRef.current) return;
      setInventories(inventoryData);
      updateStats(inventoryData);
      loadPurchaseOptions(storeId, inventoryData);
      const firstPageWasEverything = inventoryData.length < FIRST_PAGE_ROWS;
      setHydration({
        loaded: inventoryData.length,
        total: firstPageWasEverything ? inventoryData.length : null,
        complete: firstPageWasEverything,
        failed: false,
      });
      if (!firstPageWasEverything) hydrateRemainingInventories(storeId, run, inventoryData);
    } catch (error) {
      console.error('❌ [INVENTORY-MANAGER] Error fetching inventories:', error);
      setInventories([]);
      updateStats([]);
      setHydration({ loaded: 0, total: 0, complete: true, failed: true });
    } finally {
      setLoading(false);
      // Every exit lands here, a failed first page included: an error is still
      // an answer, and the empty state is honest once one has arrived.
      setFirstLoadDone(true);
    }
  };

  // ── Background hydration ──────────────────────────────────────────────
  // Pages the rest of the store in behind the first paint. Deliberately
  // sequential: a catalog sweep should not open ten parallel connections
  // while the user is trying to type in the search box.
  //
  // If this fails partway the rows already fetched stay — `complete` simply
  // never turns true, which keeps remote search switched on, so the catalog
  // degrades to "server-backed search over a partial local list" rather than
  // to a wrong answer.
  const hydrateRemainingInventories = useCallback(async (storeId, run, firstPage) => {
    let offset = firstPage.length;
    let loaded = firstPage.length;

    // A background page is merged INTO whatever state the user has since
    // produced, never over the top of it. Holding the accumulated array in a
    // closure and calling setInventories(all) would let a page that was in
    // flight during a save or a delete put the old row back — the save would
    // look like it worked and then quietly undo itself seconds later.
    //
    // The merge is deliberately not dedupeInventoryRowsById: that arbitrates by
    // `updated_at`, and an optimistic save stamps a CLIENT clock, so a skewed
    // clock lets the server's stale copy win. Rows already present locally are
    // kept as they are; only genuinely new ids are appended.
    const mergePage = (page) => {
      setInventories((current) => {
        const known = new Set(current.map((row) => row.id));
        const additions = page.filter((row) => row && row.id != null && !known.has(row.id));
        return additions.length > 0 ? current.concat(additions) : current;
      });
    };

    try {
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { data, error } = await fetchInventoryPage(storeId, { limit: HYDRATION_PAGE_ROWS, offset });
        if (run !== hydrationRunRef.current) return;
        if (error) {
          console.warn('⚠️ [INVENTORY-MANAGER] Catalog hydration stopped early:', error?.message || error);
          setHydration((current) => ({ ...current, failed: true }));
          return;
        }
        const page = data || [];
        if (page.length === 0) break;

        mergePage(page);
        loaded += page.length;
        setHydration((current) => ({ ...current, loaded, complete: false, failed: false }));
        loadPurchaseOptions(storeId, page, { append: true });
        offset += page.length;
        if (page.length < HYDRATION_PAGE_ROWS) break;

        // Yield to the event loop between pages so typing and scrolling stay
        // responsive while a large store streams in.
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
      if (run !== hydrationRunRef.current) return;
      // updateStats has to read the state that actually landed, not the local
      // tally — rows deleted mid-hydration are gone from one and not the other.
      setInventories((current) => { updateStats(current); return current; });
      setHydration((current) => ({ ...current, complete: true, failed: false }));
    } catch (error) {
      if (run !== hydrationRunRef.current) return;
      console.warn('⚠️ [INVENTORY-MANAGER] Catalog hydration failed:', error?.message || error);
      setHydration((current) => ({ ...current, failed: true }));
    }
  }, [fetchInventoryPage]);

  // Batched purchase-option load for the visible catalog. Chunked because a
  // large store's id list would otherwise blow past the URL length limit on
  // the `in` filter. Failure is recorded, not thrown: the rows still render,
  // the cost cells say so, and "Try again" re-runs this.
  //
  // `append` is for the incremental callers — each background hydration page,
  // and rows that arrive by server search. They pass only their own new rows,
  // so the result has to merge into what is already known instead of replacing
  // it; a plain replace would blank the prices of every row already on screen
  // each time another page landed.
  const loadPurchaseOptions = useCallback(async (storeId, items, { append = false } = {}) => {
    const ids = (items || []).map((item) => item.id).filter((id) => id != null);
    if (!storeId || ids.length === 0) {
      if (append) return;
      setOptionsByItemId({});
      setComparisonItemById({});
      setOptionsError(false);
      return;
    }
    setOptionsLoading(true);
    setOptionsError(false);
    try {
      const CHUNK = 300;
      const merged = {};
      const mergedComparisonItems = {};
      for (let start = 0; start < ids.length; start += CHUNK) {
        // Sequential on purpose: a catalog sweep should not open dozens of
        // parallel connections behind the user's first paint.
        // eslint-disable-next-line no-await-in-loop
        const chunkIds = ids.slice(start, start + CHUNK);
        const [grouped, comparisonItems] = await Promise.all([
          fetchOptionsForItems(storeId, chunkIds),
          fetchComparisonItems(storeId, chunkIds),
        ]);
        Object.assign(merged, grouped);
        Object.assign(mergedComparisonItems, comparisonItems);
      }
      if (append) {
        setOptionsByItemId((current) => ({ ...current, ...merged }));
        setComparisonItemById((current) => ({ ...current, ...mergedComparisonItems }));
      } else {
        setOptionsByItemId(merged);
        setComparisonItemById(mergedComparisonItems);
      }
    } catch (error) {
      console.warn('[INVENTORY-MANAGER] Purchase options unavailable (non-fatal):', error?.message);
      // An incremental failure must not throw away the options already loaded
      // for the rows the user is looking at.
      if (!append) {
        setOptionsByItemId({});
        setComparisonItemById({});
      }
      setOptionsError(true);
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  // Which packages were actually ORDERED, and when — the evidence behind the
  // Related Products "Last Used" badge. Keyed by purchase_option_id. Loaded
  // from the options already in state, so it costs one query and never blocks
  // the tab: an empty map simply means no order history, and the badge is off.
  const [orderUseByOptionId, setOrderUseByOptionId] = useState({});
  // Only the Related Products view reads this (the "Last Used" badge), so
  // nothing here runs until the user is actually there. optionsByItemId is
  // replaced wholesale on every incremental catalog page, and flattening +
  // sorting it on each of those put measurable work in front of the first
  // paint of the items list, which is the one thing that must stay fast.
  const orderUsageOptionIds = useMemo(() => {
    if (catalogView !== 'related') return [];
    return Object.values(optionsByItemId || {})
      .flat()
      .map((option) => option?.id)
      .filter((id) => id != null)
      .map(String)
      .sort();
  }, [optionsByItemId, catalogView]);
  const orderUsageKey = orderUsageOptionIds.join(',');
  useEffect(() => {
    const storeId = currentStore?.id;
    if (!storeId || orderUsageOptionIds.length === 0) return undefined;
    let cancelled = false;
    fetchLastOrderUseByOption(storeId, orderUsageOptionIds)
      .then((map) => { if (!cancelled) setOrderUseByOptionId(map || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore?.id, orderUsageKey]);

  // Vendor display names (so a row never shows a raw uuid) and the store's
  // costing method (so the cost value can name itself). Both degrade quietly.
  useEffect(() => {
    const storeId = currentStore?.id;
    if (!storeId) return undefined;
    let cancelled = false;

    Promise.resolve(supabase.from('vendors').select('id, display_name').eq('store_id', storeId))
      .then(({ data, error }) => {
        if (cancelled || error) return;
        const map = {};
        (data || []).forEach((vendor) => { if (vendor?.id != null) map[vendor.id] = vendor.display_name; });
        setVendorNameById(map);
      })
      .catch(() => {});

    Promise.resolve(supabase.from('stores').select('id, inventory_costing_method').eq('id', storeId).maybeSingle())
      .then(({ data, error }) => {
        if (cancelled || error) return;
        if (data) setStoreSettings(data);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [currentStore?.id]);

  // Same-item identity suggestions belong to Vendor Item Matching, not to the
  // relationship graph. Keep the review count available without letting this
  // workflow leak back into the Relationships tabs.
  const refreshPendingVendorMatchCount = useCallback(async (storeId) => {
    if (!storeId) { setPendingVendorMatchCount(0); return; }
    setPendingVendorMatchCount(await countPendingSuggestions(storeId));
  }, []);

  useEffect(() => {
    const storeId = currentStore?.id;
    if (!storeId) { setPendingVendorMatchCount(0); return undefined; }
    let cancelled = false;
    countPendingSuggestions(storeId)
      .then((count) => { if (!cancelled) setPendingVendorMatchCount(count); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentStore?.id]);

  // Cross-canonical relationships for the Relationships workspace —
  // alternatives and same-family links between DIFFERENT items. Degrades to
  // an empty list on any failure (older schema, no rows). Confirmed and
  // suggested rows remain separate states all the way into the two views.
  const [itemRelationships, setItemRelationships] = useState([]);
  // Stored group-level canonical elections (equivalent_group_comparisons),
  // keyed by group_key. Often empty — the table may hold no rows yet, or the
  // migration may not be deployed; canonical resolution then falls back to
  // option recency, then lowest member id (resolveCanonicalMemberId).
  const [groupComparisonByKey, setGroupComparisonByKey] = useState({});
  const refreshItemRelationships = useCallback(async (storeId) => {
    if (!storeId) { setItemRelationships([]); setGroupComparisonByKey({}); return; }
    try {
      const rows = await fetchRelationshipsForStore(storeId);
      setItemRelationships(rows || []);
    } catch {
      setItemRelationships([]);
    }
    // Never throws — degrades to {} (missing table, no rows, no access).
    setGroupComparisonByKey(await fetchEquivalentGroupComparisons(storeId));
  }, []);
  useEffect(() => {
    let cancelled = false;
    const storeId = currentStore?.id;
    if (!storeId) { setItemRelationships([]); setGroupComparisonByKey({}); return undefined; }
    fetchRelationshipsForStore(storeId)
      .then((rows) => { if (!cancelled) setItemRelationships(rows || []); })
      .catch(() => { if (!cancelled) setItemRelationships([]); });
    fetchEquivalentGroupComparisons(storeId)
      .then((byKey) => { if (!cancelled) setGroupComparisonByKey(byKey || {}); })
      .catch(() => { if (!cancelled) setGroupComparisonByKey({}); });
    return () => { cancelled = true; };
  }, [currentStore?.id]);
  // Opening the tab re-reads the graph. The effect above only ever runs at
  // mount, so `itemRelationships` was a snapshot of page-load time — and an
  // import's related-products classification keeps writing rows for 20-30
  // minutes AFTER the import review closes. That is why the first visit to
  // Related Products showed nothing and a browser refresh showed everything:
  // the refresh re-ran the mount effect, and opening the tab never did.
  useEffect(() => {
    if (catalogView !== 'related') return;
    const storeId = currentStore?.id;
    if (!storeId) return;
    refreshItemRelationships(storeId);
  }, [catalogView, currentStore?.id, refreshItemRelationships]);

  // A confirmed Equivalent endpoint can sit beyond the currently hydrated
  // inventory page. Load options/comparison facts for every relationship
  // endpoint when Related Products is open; otherwise the relationship graph
  // knows the member exists but the card has no vendor tile and appears to
  // have lost it. comparisonItemById is also a loaded-id ledger, including
  // legitimate items with zero options, so this effect does not loop.
  useEffect(() => {
    if (catalogView !== 'related' || !currentStore?.id || !itemRelationships.length) return;
    const endpointById = new Map();
    itemRelationships.forEach((row) => {
      for (const endpoint of [row?.item_a, row?.item_b]) {
        if (endpoint?.id == null || endpoint.is_active === false) continue;
        const key = String(endpoint.id);
        if (!comparisonItemById?.[key]) endpointById.set(key, endpoint);
      }
    });
    if (endpointById.size) {
      loadPurchaseOptions(currentStore.id, [...endpointById.values()], { append: true });
    }
  }, [
    catalogView, currentStore?.id, itemRelationships, comparisonItemById,
    loadPurchaseOptions,
  ]);

  // The confirmed EQUIVALENT working set: connected groups over same_item
  // rows. Empty maps when the store has no links — every consumer degrades to
  // exactly the pre-group behavior.
  const workingSetGroups = useMemo(
    () => buildWorkingSetGroups(itemRelationships),
    [itemRelationships],
  );

  // Group-pooled option lists: a grouped item's pool is the union of its
  // working-set siblings' options, so the canonical row shows the real
  // multi-vendor picture even though each member owns one option. Identity
  // map when the store has no groups.
  const pooledOptionsByItemId = useMemo(
    () => expandOptionsAcrossGroup(optionsByItemId, workingSetGroups),
    [optionsByItemId, workingSetGroups],
  );

  // group_key → stored canonical member id, for the fold's representative
  // election. Preferred when present; never required.
  const storedCanonicalByGroupKey = useMemo(() => {
    const map = {};
    Object.entries(groupComparisonByKey || {}).forEach(([groupKey, row]) => {
      if (row?.canonical_item_id !== null && row?.canonical_item_id !== undefined) {
        map[String(groupKey)] = String(row.canonical_item_id);
      }
    });
    return map;
  }, [groupComparisonByKey]);

  // One cost-and-purchasing summary per listed item. Built from the options
  // already in hand — no extra round trip per row.
  //
  // Covers rows that arrived by server search as well as paged-in ones. Built
  // from `inventories` alone, a searched-for item rendered with no price and no
  // multi-vendor lock — which is both the number the search was run to find and
  // the guard that stops a multi-vendor row offering a single editable price.
  //
  // Grouped items summarize over their group-POOLED options plus the member
  // count, so a folded row's vendor cell and expander describe the whole
  // group ("N vendors · M products") through the ordinary summary path.
  const costSummaryByItemId = useMemo(() => {
    const byId = {};
    const summarize = (item) => {
      if (!item || item.id == null || byId[item.id]) return;
      // When grouping is off, the catalog is flat — costs and badges reflect
      // the item's own vendor products only, not its folded siblings.
      const usePooled = catalogGrouped;
      const members = usePooled ? groupMembersFor(workingSetGroups, item.id) : null;
      const options = usePooled ? (pooledOptionsByItemId[item.id] || []) : (optionsByItemId[item.id] || []);
      byId[item.id] = buildItemCostSummary({
        item,
        options,
        store: storeSettings,
        vendorNameById,
        groupMemberCount: members ? members.size : 0,
      });
    };
    inventories.forEach(summarize);
    remoteSearchResults.forEach(summarize);
    return byId;
  }, [inventories, remoteSearchResults, pooledOptionsByItemId, optionsByItemId, workingSetGroups, storeSettings, vendorNameById, catalogGrouped]);

  // ── Catalog pill group editor ──────────────────────────────────────────
  // The catalog's multi-vendor pill and the Related Products tab must show
  // the SAME group with the SAME editor. This computes the folded catalog
  // groups the same way the tab does, but over the catalog's projection
  // (inventories + relationship endpoints) so a pill can open its full group
  // even when sibling items live on another page.
  const catalogProjectionItems = useMemo(() => {
    const byId = new Map();
    (Array.isArray(inventories) ? inventories : []).forEach((item) => {
      if (item?.id != null) byId.set(String(item.id), item);
    });
    (Array.isArray(itemRelationships) ? itemRelationships : []).forEach((row) => {
      for (const endpoint of [row?.item_a, row?.item_b]) {
        if (endpoint?.id == null || endpoint.is_active === false) continue;
        const key = String(endpoint.id);
        byId.set(key, { ...(byId.get(key) || {}), ...endpoint });
      }
    });
    return [...byId.values()];
  }, [inventories, itemRelationships]);

  const catalogFoldedGroups = useMemo(() => foldCatalogGroupsByEquivalence(
    buildRelatedProductCatalogGroups(catalogProjectionItems, optionsByItemId, vendorNameById),
    workingSetGroups,
    { storedCanonicalByGroupKey, optionsByItemId },
  ), [catalogProjectionItems, optionsByItemId, vendorNameById, workingSetGroups, storedCanonicalByGroupKey]);

  const storedUnitByGroupKey = useMemo(() => {
    const map = {};
    Object.entries(groupComparisonByKey || {}).forEach(([groupKey, row]) => {
      const v = String(row?.comparison_unit ?? '').trim();
      if (v) map[String(groupKey)] = v;
    });
    return map;
  }, [groupComparisonByKey]);

  const catalogEditingGroup = useMemo(() => {
    if (!catalogGroupModalKey) return null;
    return catalogFoldedGroups.find(
      (g) => String(g.equivalentGroupId ?? g.itemId) === String(catalogGroupModalKey)
    ) || catalogFoldedGroups.find((g) => String(g.itemId) === String(catalogGroupModalKey)) || null;
  }, [catalogGroupModalKey, catalogFoldedGroups]);

  const catalogGroupItemById = useMemo(() => {
    const map = new Map();
    catalogProjectionItems.forEach((item) => {
      if (item?.id != null) map.set(String(item.id), item);
    });
    return map;
  }, [catalogProjectionItems]);

  const catalogEditingComparison = useMemo(() => {
    if (!catalogEditingGroup) return { entriesByOptionId: null, unitLabel: '' };
    const item = {
      ...catalogEditingGroup.item,
      ...(comparisonItemById?.[catalogEditingGroup.itemId] || comparisonItemById?.[String(catalogEditingGroup.itemId)] || {}),
    };
    const memberIds = catalogEditingGroup.memberItemIds?.length ? catalogEditingGroup.memberItemIds : [catalogEditingGroup.itemId];
    const seenOptionIds = new Set();
    const options = [];
    memberIds.forEach((memberId) => {
      (optionsByItemId?.[memberId] || [])
        .filter((option) => option && option.status !== 'inactive')
        .forEach((option) => {
          const key = option.id != null ? String(option.id) : null;
          if (key && seenOptionIds.has(key)) return;
          if (key) seenOptionIds.add(key);
          options.push(option);
        });
    });
    const itemsById = {};
    memberIds.forEach((memberId) => {
      const base = comparisonItemById?.[memberId] || comparisonItemById?.[String(memberId)] || null;
      if (base) itemsById[String(memberId)] = base;
    });
    const groupKey = catalogEditingGroup.equivalentGroupId != null ? String(catalogEditingGroup.equivalentGroupId) : null;
    const display = buildComparablePriceDisplay({
      item,
      options,
      vendorNameById,
      itemsById,
      comparisonUnit: (groupKey && storedUnitByGroupKey[groupKey]) || null,
    });
    const unitLabel = String(display.comparisonUnitLabel || '').trim();
    const byOptionId = new Map(display.options.map((row) => [String(row.optionId), row]));
    const visible = catalogEditingGroup.products.map((product) => ({
      product,
      row: byOptionId.get(String(product.id)) || null,
    }));
    const costs = visible.map(({ row }) => row?.comparableUnitCost).filter((cost) => Number.isFinite(cost) && cost > 0);
    const { decimals: baseDecimals, subCent } = comparablePrecision(costs);
    let decimals = baseDecimals;
    while (decimals < 4 && costs.some((cost) => Number.parseFloat(roundHalfUpFixed(cost, decimals)) === 0)) {
      decimals += 1;
    }
    const blocked = visible.some(({ product, row }) => (
      product.packagePrice != null
      && !(row && Number.isFinite(row.comparableUnitCost) && row.comparableUnitCost > 0)
    ));
    const lowest = costs.length ? Math.min(...costs) : null;
    const lowestIds = new Set();
    if (unitLabel && !blocked && !subCent && costs.length > 1) {
      visible.forEach(({ product, row }) => {
        if (row && row.comparableUnitCost === lowest) lowestIds.add(String(product.id));
      });
    }
    const savings = buildGroupSavings(
      visible.map(({ product, row }) => ({
        optionId: String(product.id),
        comparableUnitCost: row?.comparableUnitCost ?? null,
      })),
      { blocked: blocked || !unitLabel },
    );
    const savingsLine = formatSavingsLine(savings, unitLabel);
    const entries = new Map();
    visible.forEach(({ product, row }) => {
      const cost = row?.comparableUnitCost;
      const priced = unitLabel && Number.isFinite(cost) && cost > 0;
      const isLowest = lowestIds.has(String(product.id));
      entries.set(String(product.id), {
        comparable: priced ? formatComparablePrice(cost, unitLabel, { decimals }) : null,
        estimated: row?.estimated === true,
        isLowest,
        isTied: lowestIds.size > 1 && isLowest,
        savingsLine: isLowest && !savings.tied ? savingsLine : null,
        isLastUsed: false,
        lastUsedTitle: null,
        unresolvedReason: priced ? null : (row?.unresolvedReason ?? 'no_stored_comparison'),
      });
    });
    // Last-used: resolve over the pooled active options for the group
    try {
      const lastUsed = resolveLastUsedOption(options, { orderUseByOptionId: {} });
      if (lastUsed) {
        const entry = entries.get(String(lastUsed.optionId));
        if (entry) {
          entry.isLastUsed = true;
          entry.lastUsedTitle = lastUsedTitle(lastUsed);
        }
      }
    } catch (_) {}
    return { entriesByOptionId: entries, unitLabel };
  }, [catalogEditingGroup, optionsByItemId, vendorNameById, comparisonItemById, storedUnitByGroupKey]);

  const updateStats = (inventoryList) => {
    setStats({
      totalItems: inventoryList.length,
      categories: [...new Set(inventoryList.map((item) => item.category).filter(Boolean))].length,
      areas: [...new Set(inventoryList.map((item) => item.location).filter(Boolean))].length,
      vendors: [...new Set(inventoryList.map((item) => item.vendor || item.brand).filter(Boolean))].length
    });
  };

  const handleFileUpload = async (info) => {
    if (currentStore?.id) {
      await fetchInventories(currentStore.id);
    }
    setIsImportToolModalOpen(false);
    setEntryMode('edit');
    const itemIds = info && Array.isArray(info.itemIds) ? info.itemIds : [];
    if (itemIds.length > 0) offerTemplateCreation(itemIds);
  };

  // After an import completes (file/photo or "Build from menus"), invite the
  // user to build count + ordering templates from just-imported items. Saying
  // yes opens CounterIntelligence AI on the Count page — the same conversation
  // a new store gets at the end of setup — so the two paths never drift.
  // Skipped when we can't scope the batch to real catalog IDs. The opt-in
  // modal lives in TemplateOfferPrompt.
  const offerTemplateCreation = (itemIds) => {
    const ids = Array.from(new Set((itemIds || []).filter(Boolean)));
    if (ids.length === 0) return;
    setTemplateOfferIds(ids);
  };

  const inventoryById = useMemo(() => {
    const byId = {};
    inventories.forEach((item) => {
      byId[item.id] = item;
    });
    return byId;
  }, [inventories]);

  const trendVendorOptions = useMemo(() => {
    const set = new Set();
    inventories.forEach((item) => {
      const v = normalizeText(item.vendor);
      if (v) set.add(v);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [inventories]);

  const trendCategoryOptions = useMemo(() => {
    const set = new Set();
    inventories.forEach((item) => {
      const c = normalizeText(item.category);
      if (c) set.add(c);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [inventories]);

  const optionSearchByItemId = useMemo(() => {
    const result = {};
    Object.entries(optionsByItemId).forEach(([itemId, options]) => {
      result[itemId] = (Array.isArray(options) ? options : [])
        .filter((option) => option && option.status !== 'inactive')
        .map((option) => ({
          vendor: normalizeText(vendorNameById[option.vendor_id] || option.vendor_name_text),
          searchable: [
            vendorNameById[option.vendor_id],
            option.vendor_name_text,
            option.vendor_description,
            option.vendor_sku,
            option.pack_description,
          ].map(normalizeText).join(' ').toLowerCase(),
        }));
    });
    return result;
  }, [optionsByItemId, vendorNameById]);

  const finderVendorOptions = useMemo(() => {
    const set = new Set(trendVendorOptions);
    remoteSearchResults.forEach((item) => {
      const v = normalizeText(item.vendor);
      if (v) set.add(v);
    });
    Object.values(optionSearchByItemId).forEach((options) => {
      options.forEach((option) => { if (option.vendor) set.add(option.vendor); });
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [trendVendorOptions, remoteSearchResults, optionSearchByItemId]);

  const finderCategoryOptions = useMemo(() => {
    const set = new Set(trendCategoryOptions);
    remoteSearchResults.forEach((item) => {
      const c = normalizeText(item.category);
      if (c) set.add(c);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [trendCategoryOptions, remoteSearchResults]);

  const trendRows = useMemo(() => {
    if (!trendsRawRows.length) return [];
    // Group rows by inventory_item_id (rows are already asc by changed_at)
    const byItem = new Map();
    trendsRawRows.forEach((row) => {
      if (!row.inventory_item_id) return;
      const list = byItem.get(row.inventory_item_id) || [];
      list.push(row);
      byItem.set(row.inventory_item_id, list);
    });

    const num = (v) => {
      if (v === null || v === undefined || v === '') return null;
      const n = typeof v === 'number' ? v : parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };

    const results = [];
    byItem.forEach((rows, itemId) => {
      const item = inventoryById[itemId];
      if (!item) return; // item deleted or not loaded — skip

      const vendor = normalizeText(item.vendor);
      const category = normalizeText(item.category);
      if (trendsVendor !== 'all' && vendor !== trendsVendor) return;
      if (trendsCategory !== 'all' && category !== trendsCategory) return;

      // Prefer case-price net change; fall back to unit-price.
      let basis = 'case';
      let earliestOld = null;
      let latestNew = null;
      for (const r of rows) {
        const o = num(r.old_case_price);
        if (o !== null && earliestOld === null) earliestOld = o;
        const n = num(r.new_case_price);
        if (n !== null) latestNew = n;
      }
      if (earliestOld === null || latestNew === null) {
        basis = 'unit';
        earliestOld = null;
        latestNew = null;
        for (const r of rows) {
          const o = num(r.old_unit_price);
          if (o !== null && earliestOld === null) earliestOld = o;
          const n = num(r.new_unit_price);
          if (n !== null) latestNew = n;
        }
      }
      if (earliestOld === null || latestNew === null || earliestOld === 0) return;

      const deltaAbs = latestNew - earliestOld;
      const deltaPct = (deltaAbs / earliestOld) * 100;
      if (deltaAbs === 0) return;
      if (trendsDirection === 'increases' && deltaAbs <= 0) return;
      if (trendsDirection === 'decreases' && deltaAbs >= 0) return;

      results.push({
        itemId,
        description: item.description || '(unnamed item)',
        vendor: vendor || '—',
        category: category || '—',
        basis,
        oldPrice: earliestOld,
        newPrice: latestNew,
        deltaAbs,
        deltaPct,
        changeCount: rows.length,
        lastChangedAt: rows[rows.length - 1].changed_at,
      });
    });

    results.sort((a, b) => Math.abs(b.deltaPct) - Math.abs(a.deltaPct));
    return results.slice(0, 25);
  }, [trendsRawRows, inventoryById, trendsVendor, trendsCategory, trendsDirection]);

  const normalizedRows = useMemo(
    () => inventories.map((item) => normalizeRow(item, inlineEdits[item.id])),
    [inlineEdits, inventories]
  );

  // Alias hits for the current finder query: itemId → matched alias. A row
  // whose alias matches is included even when none of its own fields do, and
  // the NAME cell explains why with a 'Matched vendor product' subline.
  const aliasMatchByItemId = useMemo(() => {
    const map = new Map();
    matchAliases(itemAliasMap, deferredFinderQuery).forEach((m) => {
      if (!map.has(m.itemId)) map.set(m.itemId, m.alias);
    });
    return map;
  }, [itemAliasMap, deferredFinderQuery]);

  const finderResults = useMemo(() => {
    const normalizedQuery = deferredFinderQuery.trim().toLowerCase();

    const matchesFilters = (item) => {
      const optionSearch = optionSearchByItemId[item.id] || [];
      if (finderVendor !== 'all'
        && normalizeText(item.vendor) !== finderVendor
        && !optionSearch.some((option) => option.vendor === finderVendor)) return false;
      if (finderCategory !== 'all' && normalizeText(item.category) !== finderCategory) return false;
      if (!normalizedQuery) return true;
      if (SEARCHABLE_FIELDS.some((field) => (
        normalizeText(item[field]).toLowerCase().includes(normalizedQuery)
      ))) return true;
      if (optionSearch.some((option) => option.searchable.includes(normalizedQuery))) return true;
      // Alias-aware search: the query matched one of this item's vendor aliases.
      return aliasMatchByItemId.has(item.id);
    };

    const filteredRows = normalizedRows.filter(matchesFilters);

    // Every loaded row by id — matched or not. The group fold below needs to
    // surface a group's CANONICAL row even when only a hidden sibling matched
    // the search, so the lookup must cover more than the filtered set.
    const rowById = new Map(normalizedRows.map((row) => [String(row.id), row]));

    // Rows the server found that this client has not paged in yet. Deduped
    // against what is already local by id, so a row that is both loaded and
    // returned by the search is listed once, and the LOCAL copy wins — it may
    // carry unsaved inline edits the server has never seen.
    if (!hydration.complete && normalizedQuery.length >= 2 && remoteSearchResults.length > 0) {
      const localIds = new Set(normalizedRows.map((row) => row.id));
      const extras = remoteSearchResults
        .filter((item) => !localIds.has(item.id))
        .map((item) => normalizeRow(item, inlineEdits[item.id]))
        .filter(matchesFilters);
      if (extras.length > 0) {
        filteredRows.push(...extras);
        extras.forEach((row) => { if (row?.id != null) rowById.set(String(row.id), row); });
      }
    }

    // ONE row per confirmed EQUIVALENT group when grouping is on: the
    // group is one countable, orderable thing, so the catalog lists it once
    // — as its canonical member. Members stay searchable because
    // matchesFilters already ran on every member above; the fold then swaps
    // whichever member matched for the canonical's row. When grouping is off
    // the catalog is flat — every inventory item renders independently with
    // no folded pill. Options for the raw (unpooled) recency fallback.
    const baseRows = catalogGrouped
      ? foldRowsToCanonicalMembers(filteredRows, workingSetGroups, {
          rowById,
          storedCanonicalByGroupKey,
          optionsByItemId,
        })
      : filteredRows;

    if (!SORTABLE_FIELDS.includes(sortField)) return baseRows;

    return [...baseRows].sort((left, right) => (
      compareInventoryRows(left, right, sortField, sortDirection)
    ));
  }, [deferredFinderQuery, finderVendor, finderCategory, inlineEdits, hydration.complete, remoteSearchResults, normalizedRows, sortDirection, sortField, aliasMatchByItemId, optionSearchByItemId, workingSetGroups, storedCanonicalByGroupKey, optionsByItemId, catalogGrouped]);

  // What is actually rendered. The full match list stays in memory (counts,
  // select-all and CSV all need it); only this slice becomes DOM.
  const visibleResults = useMemo(
    () => (finderResults.length > renderLimit ? finderResults.slice(0, renderLimit) : finderResults),
    [finderResults, renderLimit]
  );

  // A new query starts at the top of its own result list rather than inheriting
  // however far the previous one had been expanded.
  useEffect(() => {
    setRenderLimit(isMobileViewport ? RENDER_WINDOW_MOBILE : RENDER_WINDOW_DESKTOP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deferredFinderQuery, finderVendor, finderCategory, sortField, sortDirection]);

  const sortByHeader = useCallback((field) => {
    if (!SORTABLE_FIELDS.includes(field)) return;
    if (field === sortField) {
      setSortDirection((currentDirection) => currentDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortField(field);
    setSortDirection('asc');
  }, [sortField]);

  // Every row on screen, by id — from the paged-in catalog OR from the
  // server-side search. Editing and saving both read untouched fields from
  // here rather than from `inventoryById`, which only knows about rows the
  // catalog has paged in: seeding a draft from a map that does not contain the
  // row produced a draft of blanks, and the next save wrote those blanks.
  const itemLookupById = useMemo(() => {
    const map = { ...inventoryById };
    remoteSearchResults.forEach((item) => {
      if (item && item.id != null && !map[item.id]) map[item.id] = item;
    });
    return map;
  }, [inventoryById, remoteSearchResults]);

  const updateRowField = (itemId, field, value) => {
    setInlineEdits((prev) => ({
      ...prev,
      [itemId]: {
        ...buildEditorDraft(itemLookupById[itemId]),
        ...prev[itemId],
        [field]: value
      }
    }));
    setDirtyItemIds((prev) => (prev.includes(itemId) ? prev : [...prev, itemId]));
    setSaveNotice(null);
  };

  const discardRowChanges = (itemId) => {
    setInlineEdits((prev) => {
      if (!prev[itemId]) return prev;
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    setDirtyItemIds((prev) => prev.filter((id) => id !== itemId));
    setSaveNotice(null);
  };

  const persistItemDraft = async (itemId) => {
    // Same reason as updateRowField: a search result is a savable row even
    // though the catalog has not paged that far yet.
    const selectedItem = itemLookupById[itemId];
    if (!selectedItem || !currentStore?.id) {
      throw new Error('Store or item is missing.');
    }

    const editorDraft = inlineEdits[itemId] || buildEditorDraft(selectedItem);
    const description = normalizeText(editorDraft.description);
    if (!description) {
      throw new Error('Description is required.');
    }

    const caseQtyField = hasField(selectedItem, 'case_qty') ? 'case_qty' : 'case_quantity';
    const splitQtyField = hasField(selectedItem, 'split_qty') ? 'split_qty' : 'split_quantity';
    const unitPriceField = hasField(selectedItem, 'each_price') ? 'each_price' : 'unit_price';
    const itemCodeField = hasField(selectedItem, 'supc') ? 'supc' : 'manufacturer_code';

    const updates = {
      description,
      brand: toNullableText(editorDraft.brand),
      category: toNullableText(editorDraft.category),
      location: toNullableText(editorDraft.location),
      pack_size: toNullableText(editorDraft.pack_size),
      unit: toNullableText(editorDraft.unit),
      case_price: toNullableFloat(editorDraft.case_price)
    };

    const oldVendorName = normalizeVendorName(selectedItem.vendor);
    const vendorResolution = await assignVendorToItem({
      supabaseClient: supabase,
      storeId: currentStore.id,
      vendorName: editorDraft.vendor,
      stubNote: 'Auto-created from inventory manager edit',
      useAI: true,
      channel: 'editor',
      userId: currentUserId
    });
    updates.vendor = toNullableText(vendorResolution.vendorDisplayName || editorDraft.vendor);
    updates.vendor_id = vendorResolution.vendorId || null;
    updates[caseQtyField] = toNullableInt(editorDraft.case_qty);
    updates[splitQtyField] = toNullableInt(editorDraft.split_qty);
    updates[unitPriceField] = toNullableFloat(editorDraft.unit_price);
    updates[itemCodeField] = toNullableText(editorDraft.item_code);

    // Tri-state sales-tax flag ('' = unknown/null). Written only when the
    // value actually changed, so an unrelated edit never restamps
    // taxable_source and a user's answer is the only thing that claims 'user'
    // provenance. Deliberately kept out of the recordPriceHistory call below —
    // a taxable-only edit is not a price event.
    const draftTaxable = editorDraft.taxable === 'true'
      ? true
      : editorDraft.taxable === 'false' ? false : null;
    const currentTaxable = selectedItem.taxable === true
      ? true
      : selectedItem.taxable === false ? false : null;
    const taxableChanged = draftTaxable !== currentTaxable;
    if (taxableChanged) {
      updates.taxable = draftTaxable;
      updates.taxable_source = 'user';
    }

    let { error } = await directUpdate('inventory_items', updates, {
      id: selectedItem.id,
      store_id: currentStore.id
    });

    if (error && taxableChanged && isMissingColumnError(error)) {
      // Store behind migration 20260816120000 — save the rest of the row and
      // drop the taxable answer rather than failing the whole edit.
      console.warn('[INVENTORY-MANAGER] Taxable columns unavailable; saving row without them.');
      delete updates.taxable;
      delete updates.taxable_source;
      ({ error } = await directUpdate('inventory_items', updates, {
        id: selectedItem.id,
        store_id: currentStore.id
      }));
    }

    if (error) {
      throw error;
    }

    await recordPriceHistory({
      inventoryItemId: selectedItem.id,
      storeId: currentStore.id,
      oldName: selectedItem.description,
      newName: updates.description,
      oldCasePrice: selectedItem.case_price,
      newCasePrice: updates.case_price,
      oldUnitPrice: selectedItem[unitPriceField],
      newUnitPrice: updates[unitPriceField],
      source: 'item_editor',
    });

    const nextUpdatedAt = new Date().toISOString();
    setInventories((prev) => {
      const next = prev.map((item) => (
        item.id === selectedItem.id
          ? { ...item, ...updates, updated_at: nextUpdatedAt }
          : item
      ));
      updateStats(next);
      return next;
    });
    setInlineEdits((prev) => {
      if (!prev[itemId]) return prev;
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    setDirtyItemIds((prev) => prev.filter((id) => id !== itemId));

    // If the user typed a genuinely new vendor name (not just resolving an alias to the same vendor),
    // offer to associate the old vendor text as an alias for the new vendor.
    const newVendorDisplayName = normalizeVendorName(updates.vendor);
    const newVendorId = updates.vendor_id;
    const oldKey = (oldVendorName || '').toLowerCase();
    const newKey = (newVendorDisplayName || '').toLowerCase();
    if (
      oldKey
      && newKey
      && oldKey !== newKey
      && newVendorId
      && vendorResolution.matchType !== 'alias'
    ) {
      setAliasPrompt({
        oldVendorName,
        newVendorName: newVendorDisplayName,
        newVendorId,
        storeId: currentStore.id
      });
    }
  };

  const saveSingleRow = async (itemId) => {
    setSavingItem(true);
    setSaveNotice(null);
    try {
      await persistItemDraft(itemId);
      setSaveNotice({ type: 'success', message: 'Item updated.' });
    } catch (error) {
      console.error('❌ [INVENTORY-MANAGER] Failed to save row:', error);
      setSaveNotice({ type: 'danger', message: `Unable to save row: ${error.message}` });
    } finally {
      setSavingItem(false);
    }
  };

  const saveAllDirtyRows = async () => {
    if (dirtyItemIds.length === 0) {
      setSaveNotice({ type: 'success', message: 'No unsaved changes.' });
      return;
    }

    const idsToSave = [...dirtyItemIds];
    const failures = [];
    setSavingItem(true);
    setSaveNotice(null);

    for (const itemId of idsToSave) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await persistItemDraft(itemId);
      } catch (error) {
        failures.push({ itemId, error });
      }
    }

    if (failures.length > 0) {
      const firstError = failures[0]?.error?.message || 'Unknown error.';
      setSaveNotice({
        type: 'danger',
        message: `${failures.length} row(s) failed to save. First error: ${firstError}`
      });
    } else {
      setSaveNotice({ type: 'success', message: `Saved ${idsToSave.length} row(s).` });
    }

    setSavingItem(false);
  };

  // ── Bulk delete (soft delete) ──────────────────────────────────────────────
  // Deleting is a soft delete: the catalog rows are archived (is_active=false)
  // through the items-ai-cleanup edge function, which also drops them from every
  // count/order template while leaving history intact. Nothing is hard-deleted,
  // so support can recover a mistaken delete.
  // Select-all covers the RENDERED rows, not every row that matches. The
  // checkbox says "select all shown items" and it feeds a bulk delete, so it
  // has to mean the rows the user can actually see — silently arming 1,200
  // deletions from a screen showing 40 is not a reasonable reading of a
  // checkbox.
  //
  // DECISION — select-all acts on the FOLDED rows only: what you see is what
  // you select. A folded equivalent group contributes its one canonical row;
  // the hidden sibling ITEMS are never silently swept into a selection (or a
  // bulk delete) the user cannot see. Deleting a canonical archives that one
  // member — the survivors re-fold under the next canonical.
  const finderResultIds = useMemo(() => visibleResults.map((row) => row.id), [visibleResults]);

  const selectedVisibleCount = useMemo(
    () => finderResultIds.reduce((count, id) => (selectedItemIds.has(id) ? count + 1 : count), 0),
    [finderResultIds, selectedItemIds]
  );
  const allVisibleSelected = finderResultIds.length > 0 && selectedVisibleCount === finderResultIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;
  const selectedCount = selectedItemIds.size;

  const selectedItemsForDelete = useMemo(
    () => [...selectedItemIds].map((id) => ({
      id,
      description: normalizeText(itemLookupById[id]?.description) || `Item #${id}`,
    })),
    [selectedItemIds, itemLookupById]
  );

  const toggleRowSelected = (itemId) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        finderResultIds.forEach((id) => next.delete(id));
      } else {
        finderResultIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedItemIds(new Set());

  const openDeleteModal = () => {
    if (selectedItemIds.size === 0) return;
    setDeleteStage('confirm');
    setDeleteConfirmText('');
    setDeleteError(null);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteModalOpen(false);
    setDeleteStage('confirm');
    setDeleteConfirmText('');
    setDeleteError(null);
  };

  const deleteConfirmMatches = deleteConfirmText.trim().toLowerCase() === 'delete';

  const handleConfirmDelete = async () => {
    if (!deleteConfirmMatches || deleting) return;
    const ids = [...selectedItemIds]
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (ids.length === 0 || !currentStore?.id) {
      setDeleteError('No valid items selected.');
      return;
    }

    setDeleting(true);
    setDeleteError(null);
    try {
      const headers = await getSupabaseHeaders();
      const response = await fetch(`${SUPABASE_URL}/functions/v1/items-ai-cleanup`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'archive_selected',
          store_id: currentStore.id,
          selected_item_ids: ids,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.error) {
        throw new Error(result?.error || `Delete failed (HTTP ${response.status}).`);
      }

      const archivedIds = new Set(
        Array.isArray(result.results) && result.results.length > 0
          ? result.results.map((row) => Number(row.item_id))
          : ids
      );

      setInventories((prev) => {
        const next = prev.filter((item) => !archivedIds.has(Number(item.id)));
        updateStats(next);
        return next;
      });
      setRemoteSearchResults((prev) => prev.filter((item) => !archivedIds.has(Number(item.id))));
      setInlineEdits((prev) => {
        let changed = false;
        const next = { ...prev };
        archivedIds.forEach((id) => {
          if (next[id]) { delete next[id]; changed = true; }
        });
        return changed ? next : prev;
      });
      setDirtyItemIds((prev) => prev.filter((id) => !archivedIds.has(Number(id))));
      setSelectedItemIds(new Set());
      setDeleteModalOpen(false);
      setDeleteStage('confirm');
      setDeleteConfirmText('');
      // Relationship reads exclude archived endpoints; refresh the graph after
      // the catalog mutation so neither overview nor management keeps a stale
      // item visible.
      if (currentStore?.id) {
        refreshItemRelationships(currentStore.id);
        refreshPendingVendorMatchCount(currentStore.id);
      }

      const archivedCount = Number.isFinite(Number(result.archived_count))
        ? Number(result.archived_count)
        : archivedIds.size;
      if (archivedCount === 0) {
        setSaveNotice({
          type: 'warning',
          message: 'No items were deleted — they may have already been removed.',
        });
      } else {
        setSaveNotice({
          type: 'success',
          message: `Deleted ${archivedCount} item${archivedCount === 1 ? '' : 's'}. They no longer appear in your catalog, counts, or orders. Contact support if you need them restored.`,
        });
      }
    } catch (error) {
      console.error('❌ [INVENTORY-MANAGER] Bulk delete failed:', error);
      setDeleteError(error.message || 'Delete failed. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const downloadFinderCsv = async () => {
    if (!finderResults.length || downloadingCsv) return;
    const storeId = currentStore?.id;
    if (!storeId) {
      setSaveNotice({ type: 'danger', message: 'Store context is missing; the inventory download was not created.' });
      return;
    }

    setDownloadingCsv(true);
    setSaveNotice(null);
    try {
      // DECISION — the CSV acts on the FOLDED rows: what you see is what
      // exports. `finderResults` holds one canonical row per equivalent
      // group, so a folded-away sibling ITEM contributes no catalog row of
      // its own. Its vendor products still export, though: the download is a
      // vendor-product ledger (see inventoryCsv.js), and a sibling's option
      // is one of the visible row's pooled products — so the option fetch
      // widens to group siblings and the pools are expanded across the group
      // before projection. Twenty imported vendor products stay twenty CSV
      // rows after the pairs fold to ten visible items.
      //
      // Always take a fresh option snapshot for an export. Falling back to the
      // already-rendered canonical rows while options are still loading is
      // exactly how a 20-product import became an 11-row CSV.
      const ids = finderResults.map((row) => row.id).filter((id) => id != null);
      const fetchIdSet = new Set(ids.map(String));
      ids.forEach((id) => {
        const members = groupMembersFor(workingSetGroups, id);
        members?.forEach((memberId) => fetchIdSet.add(String(memberId)));
      });
      const fetchIds = [...fetchIdSet];
      const exportOptions = {};
      const CHUNK = 300;
      for (let start = 0; start < fetchIds.length; start += CHUNK) {
        // eslint-disable-next-line no-await-in-loop
        const grouped = await fetchOptionsForItems(storeId, fetchIds.slice(start, start + CHUNK));
        Object.assign(exportOptions, grouped);
      }
      const pooledExportOptions = expandOptionsAcrossGroup(exportOptions, workingSetGroups);

      const rawRows = buildRawInventoryCsvRows(finderResults, pooledExportOptions, vendorNameById);
      const numericKeys = new Set(['qtyPerPackage', 'splitQty', 'packagePrice', 'unitPrice']);
      const rows = rawRows.map((row) => RAW_INVENTORY_CSV_COLUMNS.map(([, key]) => {
        if (key === 'dateAdded') return csvEscape(row[key] ? formatDateAdded(row[key]) : '');
        if (numericKeys.has(key)) return csvEscape(numberForCsv(row[key]));
        return csvEscape(row[key]);
      }).join(','));
      const headers = RAW_INVENTORY_CSV_COLUMNS.map(([label]) => csvEscape(label)).join(',');
      const csv = [headers, ...rows].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `inventory-items-${dateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 0);
      setSaveNotice({
        type: 'success',
        message: `Downloaded ${rawRows.length} vendor-product row${rawRows.length === 1 ? '' : 's'} from ${finderResults.length} catalog item${finderResults.length === 1 ? '' : 's'}.`,
      });
    } catch (error) {
      console.error('❌ [INVENTORY-MANAGER] Raw inventory export failed:', error);
      setSaveNotice({
        type: 'danger',
        message: `Could not load every vendor product, so no partial CSV was downloaded. ${error?.message || ''}`.trim(),
      });
    } finally {
      setDownloadingCsv(false);
    }
  };

  const openPriceHistory = async (item) => {
    if (!item?.id) return;
    setHistoryItem(item);
    setHistoryError(null);
    setHistoryLoading(true);
    setHistoryRows([]);
    try {
      const { data, error } = await supabase
        .from('price_history')
        .select('id,changed_at,change_type,old_case_price,new_case_price,old_unit_price,new_unit_price,source,invoice_vendor,invoice_unit,old_name,new_name')
        .eq('inventory_item_id', item.id)
        .order('changed_at', { ascending: false })
        .limit(200);
      if (error) {
        setHistoryError(error.message || 'Failed to load price history.');
        setHistoryRows([]);
      } else {
        setHistoryRows(data || []);
      }
    } catch (err) {
      setHistoryError(err?.message || 'Failed to load price history.');
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closePriceHistory = () => {
    setHistoryItem(null);
    setHistoryRows([]);
    setHistoryError(null);
    setHistoryLoading(false);
  };

  const loadPriceTrends = useCallback(async (storeId, windowDays) => {
    if (!storeId) return;
    setTrendsLoading(true);
    setTrendsError(null);
    try {
      const sinceIso = new Date(Date.now() - windowDays * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('price_history')
        .select('inventory_item_id,changed_at,old_case_price,new_case_price,old_unit_price,new_unit_price,price_delta_absolute,price_delta_pct,source,invoice_vendor')
        .eq('store_id', storeId)
        .gte('changed_at', sinceIso)
        .order('changed_at', { ascending: false })
        .limit(5000);
      if (error) {
        setTrendsError(error.message || 'Failed to load price trends.');
        setTrendsRawRows([]);
      } else {
        // Re-sort ascending so the aggregation logic (earliest old → latest new) is correct.
        const asc = (data || []).slice().sort((a, b) => {
          const ta = a.changed_at ? new Date(a.changed_at).getTime() : 0;
          const tb = b.changed_at ? new Date(b.changed_at).getTime() : 0;
          return ta - tb;
        });
        setTrendsRawRows(asc);
        setTrendsLoadedAt(new Date());
      }
    } catch (err) {
      setTrendsError(err?.message || 'Failed to load price trends.');
      setTrendsRawRows([]);
    } finally {
      setTrendsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!trendsPanelOpen || !currentStore?.id) return;
    loadPriceTrends(currentStore.id, trendsWindow);
  }, [trendsPanelOpen, currentStore?.id, trendsWindow, loadPriceTrends]);

  const exportPriceHistoryCsv = () => {
    if (!historyItem || historyRows.length === 0) return;
    const headers = [
      'Event',
      'Date',
      'Previous case price',
      'Case price',
      'Previous unit price',
      'Unit price',
      'Source',
      'Vendor',
    ];
    const lines = [headers.map(csvEscape).join(',')];
    historyRows.forEach((row) => {
      lines.push([
        row.change_type === 'setup' ? 'Item added' : row.change_type === 'removed' ? 'Price removed' : 'Price updated',
        isoDateForCsv(row.changed_at),
        numberForCsv(row.old_case_price),
        numberForCsv(row.new_case_price),
        numberForCsv(row.old_unit_price),
        numberForCsv(row.new_unit_price),
        formatSource(row.source),
        row.invoice_vendor || historyItem.vendor || '',
      ].map(csvEscape).join(','));
    });
    const csvContent = `\uFEFF${lines.join('\r\n')}\r\n`;
    const today = new Date().toISOString().slice(0, 10);
    const filename = `price-history_${slugForFilename(historyItem.description)}_${today}.csv`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const scrollToItemEditor = () => {
    const target = document.getElementById('item-search-edit');
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openImportTool = (mode) => {
    if (mode === 'edit') {
      setEntryMode('edit');
      setIsImportToolModalOpen(false);
      setManualRows(makeEmptyRows(5));
      setManualSaveError(null);
      setManualPriceErrorCells(null);
      setIsManualEntryOpen(true);
      return;
    }
    setEntryMode(mode);
    setIsImportToolModalOpen(true);
  };

  const closeImportToolModal = () => {
    setIsImportToolModalOpen(false);
    if (entryMode !== 'edit') {
      setEntryMode('edit');
    }
  };

  const updateManualRow = (rowIndex, field, value) => {
    setManualRows(prev => prev.map((row, i) => i === rowIndex ? { ...row, [field]: value } : row));
    if (field === 'case_price' || field === 'unit_price') {
      setManualPriceErrorCells(prev => {
        if (!prev) return prev;
        const key = `${rowIndex}:${field}`;
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next.size > 0 ? next : null;
      });
    }
  };

  const addManualRows = (count) => {
    setManualRows(prev => [...prev, ...makeEmptyRows(count)]);
  };

  // Build the LinkItemDialog review queue for rows whose name looks like an
  // existing catalog item (candidates at or above the suggest threshold).
  // Candidates are enriched with live purchase-option info (current vendors /
  // packs) — non-fatal when that fetch fails. Returns null when no row needs
  // review.
  const buildLinkReviewQueue = async (contexts) => {
    const queueIdxs = [];
    contexts.forEach((ctx, idx) => { if (ctx.candidates.length > 0) queueIdxs.push(idx); });
    if (queueIdxs.length === 0) return null;

    const candidateIds = [...new Set(
      queueIdxs.flatMap((idx) => contexts[idx].candidates.map((c) => c.itemId)).filter(Boolean)
    )];
    let optionsByItem = {};
    try {
      optionsByItem = await fetchOptionsForItems(currentStore.id, candidateIds);
    } catch (err) {
      console.warn('⚠️ [Manual Entry] Could not load candidate purchase options:', err?.message || err);
    }
    const enrichedCandidates = {};
    queueIdxs.forEach((idx) => {
      enrichedCandidates[idx] = contexts[idx].candidates.map((candidate) => {
        const existing = inventoryById[candidate.itemId] || {};
        const options = (optionsByItem[candidate.itemId] || [])
          .filter((o) => !o.status || o.status === 'active');
        return {
          ...candidate,
          category: existing.category || '',
          unit: existing.unit || '',
          packSize: existing.pack_size || '',
          vendors: [...new Set(options.map((o) => (o.vendor_description || '').trim()).filter(Boolean))],
          packs: [...new Set(options.map((o) => (o.pack_description || '').trim()).filter(Boolean))],
        };
      });
    });
    return { contexts, queueIdxs, cursor: 0, resolutions: {}, enrichedCandidates };
  };

  // The write phase of manual entry, run after any cross-reference review:
  // rows resolved 'separate' (or never flagged) are inserted as new items;
  // rows resolved 'link' only gain a purchase option on the chosen item;
  // rows resolved 'abort' are dropped.
  const finishManualEntrySave = async (contexts, resolutions) => {
    const insertCtxs = contexts.filter((_, idx) => resolutions[idx]?.type !== 'link' && resolutions[idx]?.type !== 'abort');
    const linkCtxs = contexts
      .map((ctx, idx) => ({ ctx, resolution: resolutions[idx] }))
      .filter((entry) => entry.resolution?.type === 'link');
    if (insertCtxs.length === 0 && linkCtxs.length === 0) {
      setManualSaveError('Nothing to save — every row was skipped.');
      return;
    }
    setManualSaving(true);
    try {
      if (insertCtxs.length > 0) {
        const trimmedLocations = [...new Set(insertCtxs
          .map(({ row }) => row.location.trim())
          .filter(Boolean))];
        const trimmedCategories = [...new Set(insertCtxs
          .map(({ row }) => row.category.trim())
          .filter(Boolean))];

        const { data: existingStoreDetails } = await supabase
          .from('store_details')
          .select('id, locations, accounting_categories')
          .eq('store_id', currentStore.id)
          .limit(1);

        const existingDetailsRow = existingStoreDetails?.[0] || null;
        const existingLocations = Array.isArray(existingDetailsRow?.locations)
          ? existingDetailsRow.locations.filter((value) => typeof value === 'string' && value.trim())
          : [];
        const existingCategories = Array.isArray(existingDetailsRow?.accounting_categories)
          ? existingDetailsRow.accounting_categories.filter((value) => typeof value === 'string' && value.trim())
          : [];

        const mergedLocations = [...new Set([
          ...existingLocations,
          ...trimmedLocations
        ])];
        const mergedCategories = [...new Set([
          ...existingCategories,
          ...trimmedCategories
        ])];

        if (existingDetailsRow?.id) {
          await supabase
            .from('store_details')
            .update({
              locations: mergedLocations,
              accounting_categories: mergedCategories,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingDetailsRow.id);
        } else {
          await supabase
            .from('store_details')
            .insert({
              store_id: currentStore.id,
              locations: mergedLocations,
              accounting_categories: mergedCategories
            });
        }
      }

      const items = insertCtxs.map(({ row, prices }) => ({
        store_id: currentStore.id,
        description: row.description.trim(),
        category: row.category.trim(),
        unit: row.unit.trim(),
        location: row.location.trim(),
        brand: row.brand.trim() || null,
        vendor: row.vendor.trim() || null,
        case_price: prices.case_price,
        unit_price: prices.unit_price,
        case_quantity: toNullableInt(row.case_qty),
        split_quantity: toNullableInt(row.split_qty),
        pack_size: normalizeText(row.pack_size) || null,
        user_defined_name: true,
        created_source: 'manual',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));

      // A 'link' row creates NO item — only a purchase option on the chosen
      // existing item, built from the draft fields the user typed.
      const linkDrafts = linkCtxs.map(({ ctx, resolution }) => ({
        id: resolution.itemId,
        description: ctx.row.description.trim(),
        brand: ctx.row.brand.trim() || null,
        vendor: ctx.row.vendor.trim() || null,
        unit: ctx.row.unit.trim() || null,
        pack_size: normalizeText(ctx.row.pack_size) || null,
        case_price: ctx.prices.case_price,
        unit_price: ctx.prices.unit_price,
        case_quantity: toNullableInt(ctx.row.case_qty),
      }));

      for (const item of [...items, ...linkDrafts]) {
        if (!item.vendor) {
          item.vendor_id = null;
          continue;
        }
        try {
          const vendorResolution = await assignVendorToItem({
            supabaseClient: supabase,
            storeId: currentStore.id,
            vendorName: item.vendor,
            stubNote: 'Auto-created from manual item entry',
            useAI: true,
            channel: 'editor',
            userId: currentUserId
          });
          item.vendor_id = vendorResolution.vendorId || null;
          item.vendor = vendorResolution.vendorDisplayName || item.vendor;
        } catch (vendorError) {
          console.warn('⚠️ [Manual Entry] Failed to resolve vendor:', vendorError?.message || vendorError);
          item.vendor_id = null;
        }
      }

      let insertedItems = [];
      let creationOutcomes = [];
      if (items.length > 0) {
        const { data, error } = await insertInventoryItems(items, 'id,description,vendor,vendor_id,unit,pack_size,brand,case_price,case_quantity,unit_price');
        if (error) throw error;
        insertedItems = data || [];
        creationOutcomes = await resolveInventoryInsertResults(
          currentStore.id,
          items,
          insertedItems,
          { catalog: inventories },
          supabase,
        );
      }

      for (const inserted of insertedItems) {
        const insertedCasePrice = parseFloat(inserted.case_price);
        const insertedUnitPrice = parseFloat(inserted.unit_price);
        const hasPrice = (Number.isFinite(insertedCasePrice) && insertedCasePrice > 0) ||
                         (Number.isFinite(insertedUnitPrice) && insertedUnitPrice > 0);
        if (hasPrice) {
          await recordPriceHistory({
            inventoryItemId: inserted.id,
            storeId: currentStore.id,
            oldName: null,
            newName: inserted.description,
            oldCasePrice: null,
            newCasePrice: Number.isFinite(insertedCasePrice) && insertedCasePrice > 0 ? insertedCasePrice : null,
            oldUnitPrice: null,
            newUnitPrice: Number.isFinite(insertedUnitPrice) && insertedUnitPrice > 0 ? insertedUnitPrice : null,
            source: 'item_editor',
          });
        }
      }

      // Mirror each new manual item into a purchase option (its own way-to-buy).
      // No catalog is passed: cross-reference review now happens BEFORE insert
      // (LinkItemDialog), so re-recording suggestions here would only resurface
      // candidates the user already reviewed and kept separate. Best-effort:
      // a failure here must never fail the add-item flow.
      for (const outcome of creationOutcomes) {
        if (!outcome.item?.id) {
          console.warn('⚠️ [Manual Entry] Could not resolve a duplicate-trigger insert result:', outcome.source?.description);
          continue;
        }
        try {
          await createOptionForItem(currentStore.id, {
            ...outcome.item,
            ...outcome.source,
            id: outcome.item.id,
          }, { channel: 'manual' });
        } catch (crossRefError) {
          console.warn('⚠️ [Manual Entry] Cross-reference option recording failed:', crossRefError?.message || crossRefError);
        }
      }

      // Server-side AI matching for the freshly created items (fire-and-forget):
      // the edge function persists its own pending suggestion rows (channel
      // 'manual'), which surface in Manage Relationships on next load.
      // Never blocks or fails the save — the service warns and returns [] when
      // the function is not deployed.
      if (insertedItems.length > 0) {
        fetchAiMatchesForItems(currentStore.id, insertedItems.map((inserted) => inserted.id));
      }

      // 'link' rows: create ONLY a purchase option on the chosen existing item.
      for (const draft of linkDrafts) {
        try {
          await createOptionForItem(currentStore.id, draft, { channel: 'manual' });
        } catch (linkError) {
          console.warn('⚠️ [Manual Entry] Linking purchase option failed:', linkError?.message || linkError);
        }
      }

      clearVendorDirectoryCache(currentStore.id);
      await fetchInventories(currentStore.id);
      setIsManualEntryOpen(false);
    } catch (err) {
      setManualSaveError(err.message || 'Save failed. Please try again.');
    } finally {
      setManualSaving(false);
    }
  };

  const handleManualEntrySave = async () => {
    if (!currentStore?.id) return;
    setManualSaveError(null);
    setManualPriceErrorCells(null);
    const validRows = manualRows.filter(r =>
      r.description.trim() && r.category.trim() && r.unit.trim() && r.location.trim()
    );
    if (validRows.length === 0) {
      setManualSaveError('Fill in at least one row with Name, Category, Unit and Location.');
      return;
    }
    const parsePrice = (raw) => {
      const s = (raw ?? '').toString().trim();
      if (!s) return { ok: true, value: null };
      const n = Number(s);
      if (!Number.isFinite(n) || n < 0) return { ok: false, value: null };
      return { ok: true, value: n };
    };
    const parsedByOriginalIdx = new Map();
    const badPriceCells = new Set();
    let firstBadRowNumber = null;
    manualRows.forEach((row, origIdx) => {
      const isValidRow = row.description.trim() && row.category.trim() && row.unit.trim() && row.location.trim();
      if (!isValidRow) return;
      const cp = parsePrice(row.case_price);
      const up = parsePrice(row.unit_price);
      if (!cp.ok) badPriceCells.add(`${origIdx}:case_price`);
      if (!up.ok) badPriceCells.add(`${origIdx}:unit_price`);
      if ((!cp.ok || !up.ok) && firstBadRowNumber === null) firstBadRowNumber = origIdx + 1;
      parsedByOriginalIdx.set(origIdx, { case_price: cp.value, unit_price: up.value });
    });
    if (badPriceCells.size > 0) {
      setManualPriceErrorCells(badPriceCells);
      setManualSaveError(`Row ${firstBadRowNumber}: price must be a non-negative number.`);
      return;
    }
    setManualPriceErrorCells(null);
    const parsedPrices = validRows.map((row) => {
      const origIdx = manualRows.indexOf(row);
      return parsedByOriginalIdx.get(origIdx);
    });

    // Cross-reference check BEFORE anything is written: a row that looks like
    // an existing item is reviewed in the LinkItemDialog (queued sequentially)
    // instead of being blindly inserted as a duplicate.
    const crossRefCatalog = inventories.map((item) => ({
      id: item.id,
      description: item.description,
      brand: item.brand,
      unit: item.unit,
      pack_size: item.pack_size,
      vendor: item.vendor,
      strict_specification: item.strict_specification,
    }));
    const contexts = validRows.map((row, idx) => ({
      row,
      prices: parsedPrices[idx],
      candidates: findCrossReferenceCandidates(crossRefCatalog, {
        name: row.description.trim(),
        vendor: row.vendor.trim() || null,
        unit: row.unit.trim() || null,
        packSize: normalizeText(row.pack_size) || null,
        brand: row.brand.trim() || null,
      }),
    }));

    setManualSaving(true);
    try {
      // AI cross-reference (best-effort): the items-ai-match edge function
      // decides matches the deterministic token matcher can't see (vendor
      // tokens, package sizes) and returns them per line. AI candidates are
      // merged into the LinkItemDialog queue (deduped by item id, AI entry
      // preferred); when the function is unavailable the service returns []
      // and the deterministic flow proceeds exactly as before.
      const aiMatches = await fetchAiMatchesForLines(
        currentStore.id,
        validRows.map((row, idx) => ({
          ref: idx,
          name: row.description.trim(),
          vendor: row.vendor.trim() || null,
          brand: row.brand.trim() || null,
          pack: normalizeText(row.pack_size) || null,
          unit: row.unit.trim() || null,
          price: parsedPrices[idx]?.case_price ?? parsedPrices[idx]?.unit_price ?? null,
        })),
      );
      if (aiMatches.length > 0) {
        const matchesByRef = new Map();
        aiMatches.forEach((match) => {
          const list = matchesByRef.get(match.line_ref) || [];
          list.push(match);
          matchesByRef.set(match.line_ref, list);
        });
        contexts.forEach((ctx, idx) => {
          ctx.candidates = mergeAiCandidates(ctx.candidates, matchesByRef.get(idx) || []);
        });
      }

      const review = await buildLinkReviewQueue(contexts);
      if (review) {
        setLinkReview(review);
        return;
      }
    } finally {
      setManualSaving(false);
    }
    await finishManualEntrySave(contexts, {});
  };

  // ── Row presentation helpers ──────────────────────────────────────────
  // Shared by the phone card and (for the actions) the desktop table, so the
  // two surfaces cannot drift into disagreeing about what a row says.

  // The one number the catalog exists to show. Prefer the costed figure the
  // purchasing pipeline computed; fall back to the item's own unit price,
  // then to its package price stated AGAINST ITS REAL PACK — a sheet that
  // prices "175 ct case — $31.95" has priced the case, and rows imported
  // that way carry case_price only (unit_price stays null by design: never a
  // model-invented per-unit division). Showing the package price as
  // "$31.95/175 ct case" is honest; showing "—" read as "the import lost the
  // prices". Say "per vendor" rather than inventing a single number for a
  // multi-vendor item whose cost has not resolved.
  const describeRowPrice = useCallback((row, summary) => {
    if (summary?.currentCost?.available) {
      const formatted = formatUnitPrice(summary.currentCost.amount, summary.countUnit || row.unit);
      if (formatted) {
        const [amount, suffix] = formatted.split('/');
        return { amount, suffix: suffix ? `/${suffix}` : null };
      }
    }
    if (summary?.isMultiVendor) return { amount: 'per vendor', suffix: null };
    const own = formatUnitPrice(row.unit_price, row.unit);
    if (own) {
      const [amount, suffix] = own.split('/');
      return { amount, suffix: suffix ? `/${suffix}` : null };
    }
    const casePrice = Number.parseFloat(row.case_price);
    if (Number.isFinite(casePrice) && casePrice > 0) {
      const packLabel = String(row.pack_size || '').trim() || 'case';
      return { amount: formatPrice(casePrice), suffix: `/${packLabel}` };
    }
    return { amount: '—', suffix: null };
  }, []);

  // A canonical item bought from several vendors has no single vendor name, and
  // printing one would silently pick a winner. A row standing in for a folded
  // EQUIVALENT group additionally states how many pooled vendor products it
  // speaks for — "2 vendors · 3 products" — because the products live on
  // sibling items the fold has hidden.
  const describeRowVendor = useCallback((row, summary) => {
    const grouped = (summary?.linkage?.groupMemberCount || 0) >= 2;
    if (summary?.isMultiVendor || grouped) {
      const vendors = summary.optionsSummary.vendorCount || 1;
      const products = summary.optionsSummary.total || vendors;
      return {
        label: grouped
          ? `${vendors} vendor${vendors === 1 ? '' : 's'} · ${products} product${products === 1 ? '' : 's'}`
          : `${vendors} vendors`,
        isMulti: true,
      };
    }
    return { label: row.vendor || summary?.preferred?.vendorLabel || 'No vendor', isMulti: false };
  }, []);

  // Everything the collapsed card leaves out, in the order a person reads it.
  const mobileDetailFields = useCallback((row) => ([
    { label: 'Brand', value: row.brand },
    { label: 'Pack / size', value: row.pack_size },
    { label: 'Case qty', value: row.case_qty },
    { label: 'Split qty', value: row.split_qty },
    { label: 'Case $', value: formatPrice(row.case_price) },
    { label: 'Code', value: row.item_code },
    {
      label: 'Taxed',
      // An unanswered item is not taxed in effect, so the card says so rather
      // than showing a bare dash that reads as missing data.
      value: row.taxable === true || row.taxable === 'true'
        ? 'Taxed'
        : row.taxable === false || row.taxable === 'false'
          ? 'Not taxed'
          : 'Not set — not taxed',
    },
    { label: 'Date added', value: formatDateAdded(row.created_at) },
  ]), []);

  // The row's actions, as menu entries. One definition drives the phone card's
  // kebab and the table's, so an action can never exist on one and not the other.
  //
  // Deliberately NOT memoized: these entries close over saveSingleRow and
  // discardRowChanges, which in turn read `inlineEdits`. Memoizing on a narrow
  // dependency list handed the menu a stale closure, and "Save" then wrote the
  // row as it stood before the edit the user had just typed.
  const rowActionItems = (row, { isDirty, isSelected }) => ([
    {
      key: 'save',
      label: 'Save changes',
      icon: '✓',
      disabled: savingItem || !isDirty,
      onSelect: () => saveSingleRow(row.id),
    },
    {
      key: 'revert',
      label: 'Revert changes',
      icon: '↺',
      disabled: savingItem || !isDirty,
      onSelect: () => discardRowChanges(row.id),
    },
    {
      key: 'history',
      label: 'Price history',
      icon: '🕘',
      onSelect: () => openPriceHistory(itemLookupById[row.id] || row),
    },
    {
      key: 'options',
      label: 'Vendor options',
      icon: '🏷',
      title: 'Ways to buy this item — vendors, packs, prices',
      onSelect: () => setPurchaseOptionsItem(itemLookupById[row.id] || row),
    },
    {
      key: 'select',
      label: isSelected ? 'Deselect item' : 'Select item',
      icon: isSelected ? '☑' : '☐',
      onSelect: () => toggleRowSelected(row.id),
    },
  ]);

  // "Show more" is deliberately a button rather than infinite scroll: this list
  // is edited, and a scroll handler that grows the DOM under a focused input is
  // how you lose someone's half-typed price.
  const renderShowMore = () => {
    if (visibleResults.length >= finderResults.length) return null;
    const remaining = finderResults.length - visibleResults.length;
    const step = isMobileViewport ? RENDER_WINDOW_MOBILE : RENDER_WINDOW_DESKTOP;
    const next = Math.min(step, remaining);
    return (
      <button
        type="button"
        className="btn btn-outline-secondary finder-load-more"
        onClick={() => setRenderLimit((current) => current + step)}
      >
        Show {next} more <span className="finder-load-more-count">({remaining.toLocaleString()} not shown)</span>
      </button>
    );
  };

  // Resolve the dialog row currently under review: record the decision and
  // advance the queue; when the queue is exhausted, run the actual save.
  const resolveLinkReviewRow = async (resolution) => {
    if (!linkReview) return;
    const ctxIdx = linkReview.queueIdxs[linkReview.cursor];
    const resolutions = { ...linkReview.resolutions, [ctxIdx]: resolution };
    if (linkReview.cursor + 1 < linkReview.queueIdxs.length) {
      setLinkReview({ ...linkReview, cursor: linkReview.cursor + 1, resolutions });
      return;
    }
    setLinkReview(null);
    await finishManualEntrySave(linkReview.contexts, resolutions);
  };

  const handleAliasConfirm = async () => {
    if (!aliasPrompt) return;
    const { storeId, newVendorId, newVendorName, oldVendorName } = aliasPrompt;
    try {
      // 1. Create the alias entry so future imports/lookups resolve correctly
      await createVendorAliasIfNeeded({
        supabaseClient: supabase,
        storeId,
        vendorId: newVendorId,
        aliasName: oldVendorName,
        channel: 'editor',
        userId: currentUserId
      });

      // 2. Bulk-update ALL existing items in this store that still carry the old
      //    vendor text so they immediately show the canonical name.
      const { error: bulkErr } = await supabase
        .from('inventory_items')
        .update({
          vendor: newVendorName,
          vendor_id: newVendorId,
          updated_at: new Date().toISOString()
        })
        .eq('store_id', storeId)
        .ilike('vendor', oldVendorName);

      if (bulkErr) {
        console.warn('[handleAliasConfirm] Bulk item update failed:', bulkErr.message);
      } else {
        // Refresh the list so the UI reflects the renamed vendors
        await fetchInventories(storeId);
      }
    } catch (aliasError) {
      console.warn('Failed to create vendor alias:', aliasError?.message);
    }
    setAliasPrompt(null);
  };

  const handleAliasDismiss = () => setAliasPrompt(null);

  return (
    <div className={`inventory-manager${isImportToolModalOpen ? ' modal-open' : ''}`}>
      <style>{`
        .inventory-manager .import-toolbar {
          display: flex;
          flex-wrap: wrap;
          gap: 0.55rem;
          margin-bottom: 0.9rem;
        }
        .inventory-manager .import-option {
          border: 1px solid #e6ddd5;
          border-radius: 999px;
          background: #fff;
          color: #5A4A42;
          font-size: 0.82rem;
          font-weight: 700;
          line-height: 1.2;
          padding: 0.48rem 0.72rem;
          display: inline-flex;
          align-items: center;
          gap: 0.38rem;
          white-space: nowrap;
          transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
          min-height: 42px;
        }
        .inventory-manager .import-option:hover {
          border-color: #526B7C;
          box-shadow: 0 3px 10px rgba(90, 74, 66, 0.12);
        }
        .inventory-manager .import-option.active {
          border-color: #526B7C;
          background: #F5EDE6;
        }
        .inventory-manager .import-option-icon {
          font-size: 1rem;
          line-height: 1;
        }
        .inventory-manager .flow-help {
          font-size: 0.84rem;
          color: #5A4A42;
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 10px;
          padding: 0.6rem 0.75rem;
          margin-bottom: 0.75rem;
        }
        .import-tool-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 1400;
          background: rgba(31, 24, 20, 0.54);
          display: flex;
          align-items: center;
          justify-content: center;
          padding-top: calc(env(safe-area-inset-top, 0px) + 1rem);
          padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 1rem);
          padding-left: 1rem;
          padding-right: 1rem;
          isolation: isolate;
          font-family: 'Montserrat', sans-serif;
        }
        .inventory-manager.modal-open .finder-table-wrap,
        .inventory-manager.modal-open .finder-controls,
        .inventory-manager.modal-open .import-toolbar {
          pointer-events: none;
          user-select: none;
        }
        .import-tool-modal {
          width: min(960px, 100%);
          max-height: min(92vh, 880px);
          background: #FFFDF9;
          border: 1px solid #EFE4DA;
          border-radius: 16px;
          box-shadow: 0 26px 64px rgba(0, 0, 0, 0.25);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .import-tool-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.75rem;
          padding: 0.85rem 1.1rem;
          border-bottom: 1px solid #E8DBCE;
          background: #FAF3EC;
          flex: 0 0 auto;
        }
        .import-tool-modal-header h6 {
          margin: 0;
          font-weight: 700;
          color: #5A4A42;
          font-size: 0.98rem;
        }
        .import-tool-modal-close {
          border: 1px solid #D8C8B8;
          border-radius: 999px;
          background: #fff;
          color: #5A4A42;
          width: 34px;
          height: 34px;
          line-height: 1;
          font-size: 1.2rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s, border-color 0.15s;
        }
        .import-tool-modal-close:hover {
          background: #F5EDE3;
          border-color: #B89A7E;
        }
        .import-tool-modal-body {
          flex: 1 1 auto;
          min-height: 0;
          overflow: auto;
          padding: 1rem 1.1rem;
          padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
          background: #FFFDF9;
        }
        /* Collapse nested scroll: when the import-approval table is rendered
           inside this modal, let the modal body be the single scroll surface. */
        .import-tool-modal-body div[style*="55vh"] {
          max-height: none !important;
          overflow: visible !important;
        }
        .import-tool-modal-body .preview-results > div[style*="border: 1px solid #EFE4DA"],
        .import-tool-modal-body .preview-results > div[style*="border:1px solid #EFE4DA"] {
          border-radius: 12px;
        }
        .inventory-manager .finder-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 1rem;
        }
        .inventory-manager .finder-results {
          border: 1px solid #e8dfd7;
          border-radius: 12px;
          background: #fff;
          overflow: hidden;
        }
        .inventory-manager .finder-table-wrap {
          border-radius: 12px;
          border: 1px solid #e8dfd7;
          background: #fff;
          overflow: hidden;
        }
        .inventory-manager .finder-table-scroll {
          max-height: 540px;
          overflow: auto;
          /* macOS hides overlay scrollbars until you scroll, which is most of
             why people never discover that this table scrolls sideways at all.
             Force a visible one, in the palette. */
          scrollbar-width: thin;
          scrollbar-color: #C9BCB0 #F5F0EB;
        }
        .inventory-manager .finder-table-scroll::-webkit-scrollbar {
          height: 10px;
          width: 10px;
        }
        .inventory-manager .finder-table-scroll::-webkit-scrollbar-track {
          background: #F5F0EB;
        }
        .inventory-manager .finder-table-scroll::-webkit-scrollbar-thumb {
          background: #C9BCB0;
          border-radius: 5px;
        }
        .inventory-manager .finder-table-scroll:focus-visible {
          outline: 2px solid #8A5A00;
          outline-offset: -2px;
        }
        .inventory-manager .finder-table {
          margin-bottom: 0;
          table-layout: fixed;
          width: 100%;
          /* Force horizontal scroll (instead of crushed/clipped columns) on
             narrow viewports; the sticky ACTIONS column stays visible.
             Raised from 1080px: that figure was below the sum of what the
             columns actually need, so table-layout:fixed shrank them all to
             fit and clipped the contents instead of scrolling. It must stay at
             or above the sum of the colgroup widths plus cell padding, or fixed
             layout starts redistributing — and clipping — again. */
          /* Tracks the colgroup: 1464px of columns plus the same cushion this
             carried before Taxed went from 58px to 52px. Narrowing a column
             without narrowing this here would hand the 6px straight back. */
          min-width: 1554px;
        }
        .inventory-manager .finder-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #faf6f2;
          color: #6a5c53;
          font-size: 0.64rem;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          border-bottom: 1px solid #e7ddd4;
          white-space: nowrap;
          padding: 0.25rem 0.2rem;
        }
        .inventory-manager .finder-sort-header {
          display: flex;
          align-items: center;
          min-width: 0;
          gap: 0.05rem;
        }
        .inventory-manager .finder-sort-button {
          flex: 1 1 auto;
          min-width: 0;
          max-width: 100%;
          border: 0;
          border-radius: 4px;
          background: transparent;
          color: inherit;
          font: inherit;
          font-weight: 700;
          letter-spacing: inherit;
          text-transform: inherit;
          line-height: 1.1;
          padding: 0.18rem 0.12rem;
          display: inline-flex;
          align-items: center;
          gap: 0.12rem;
          cursor: pointer;
          white-space: nowrap;
        }
        .inventory-manager .finder-sort-button > span:first-child {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .inventory-manager .finder-sort-button:hover {
          background: #eee5dd;
          color: #493b33;
        }
        .inventory-manager .finder-sort-button:focus-visible {
          outline: 2px solid #8A5A00;
          outline-offset: 1px;
        }
        .inventory-manager .finder-sort-button.active {
          color: #493b33;
        }
        .inventory-manager .finder-sort-indicator {
          flex: 0 0 auto;
          min-width: 0.55rem;
          color: #9a887b;
          font-size: 0.58rem;
          text-align: center;
        }
        .inventory-manager .finder-sort-button.active .finder-sort-indicator {
          color: #8A5A00;
        }
        .inventory-manager .finder-table td {
          vertical-align: middle;
          border-bottom: 1px solid #f3ede8;
          padding: 0.12rem 0.2rem;
          font-size: 0.7rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .inventory-manager .finder-table tbody tr:last-child td {
          border-bottom: none;
        }
        .inventory-manager .finder-input,
        .inventory-manager .finder-input-num {
          min-width: 0;
          width: 100%;
          font-size: 0.7rem;
          padding: 0.08rem 0.25rem;
          height: 1.4rem;
          min-height: 1.4rem;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .inventory-manager .finder-input-num {
          text-align: right;
        }
        /* Column widths are absolute, not percentages.
           Percentages made every column a fraction of a 1080px minimum, so the
           price columns were 48px — four characters — and "142.80" rendered as
           "142...". Case Qty and Case $ both collapsed to "CA...", which made
           two different columns read identically. Every column here has a
           knowable maximum content width, so it gets that width; the table
           scrolls horizontally rather than lying about its contents. */
        .inventory-manager .finder-table col.col-select { width: 34px; }
        .inventory-manager .finder-table col.col-name { width: 240px; }
        .inventory-manager .finder-table col.col-vendor { width: 132px; }
        .inventory-manager .finder-table col.col-category { width: 104px; }
        /* Y / N / blank needs about 40px, but the heading is the floor here:
           "TAXED" plus its sort arrow measures 51px, so the column cannot go
           below that without the label spilling into Date Added. Trimming the
           padding on this one heading is what buys the 58px → 52px. */
        .inventory-manager .finder-table col.col-taxable { width: 52px; }
        .inventory-manager .finder-table thead th.th-taxable {
          padding-left: 0.1rem;
          padding-right: 0.1rem;
        }
        .inventory-manager .finder-table thead th.th-taxable .finder-sort-button {
          padding-left: 0;
          padding-right: 0;
        }
        .inventory-manager .finder-table col.col-date { width: 78px; }
        .inventory-manager .finder-table col.col-area { width: 96px; }
        .inventory-manager .finder-table col.col-brand { width: 96px; }
        .inventory-manager .finder-table col.col-packsize { width: 96px; }
        /* Wide enough that "CASE QTY" wraps to two lines rather than three:
           the sort arrow and the help icon share this row, and squeezing them
           into 62px broke the label into CA / SE / QTY. */
        .inventory-manager .finder-table col.col-unit { width: 70px; }
        .inventory-manager .finder-table col.col-caseqty { width: 74px; }
        .inventory-manager .finder-table col.col-splitqty { width: 74px; }
        .inventory-manager .finder-table col.col-caseprice { width: 88px; }
        .inventory-manager .finder-table col.col-unitprice { width: 88px; }
        .inventory-manager .finder-table col.col-code { width: 96px; }
        .inventory-manager .finder-table col.col-actions { width: 46px; }
        /* A column heading is the one string in the table that cannot be
           inferred from context, so it wraps rather than ellipsizing. */
        .inventory-manager .finder-table thead th {
          padding: 0.3rem 0.25rem;
          vertical-align: bottom;
        }
        .inventory-manager .finder-sort-header { align-items: flex-end; }
        .inventory-manager .finder-sort-button {
          white-space: normal;
          align-items: flex-end;
        }
        .inventory-manager .finder-sort-button > span:first-child {
          overflow: visible;
          text-overflow: clip;
          white-space: normal;
          line-height: 1.15;
          /* Wrap only where a break belongs. Without this the label broke
             mid-word — "PACK/SIZE" came out as "PACK/SI" over "ZE". */
          overflow-wrap: normal;
          word-break: keep-all;
        }
        .inventory-manager .finder-sort-indicator { align-self: flex-end; }
        /* Decimal points line up down a price column. */
        .inventory-manager .finder-input-num { font-variant-numeric: tabular-nums; }
        /* Sticky right ACTIONS column: the four row buttons (Save/Revert/
           History/Buy) stay visible while the table scrolls horizontally.
           Cells need an opaque background matching header/row striping so the
           scrolled-under columns don't bleed through. */
        .inventory-manager .finder-table th.finder-actions-cell,
        .inventory-manager .finder-table td.finder-actions-cell {
          position: sticky;
          right: 0;
          box-shadow: -6px 0 8px -6px rgba(90, 74, 66, 0.45);
        }
        .inventory-manager .finder-table thead th.finder-actions-cell {
          z-index: 3;
          background: #faf6f2;
        }
        .inventory-manager .finder-table td.finder-actions-cell {
          z-index: 1;
          background-color: #fff;
        }
        .inventory-manager .finder-table tbody tr:nth-child(even) td.finder-actions-cell {
          background-color: #FAF6F2;
        }
        .inventory-manager .finder-alias-subline {
          font-size: 0.68rem;
          color: #8B7B73;
          padding: 0 0.3rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        /* Multi-vendor rows: the vendor cell opens the item's vendor products
           instead of pretending there is one vendor to type in. */
        .inventory-manager .finder-multivendor-btn {
          width: 100%;
          text-align: left;
          background: #F5F0EB;
          color: #5A4A42;
          border: 1px solid #D9CEC2;
          border-radius: 7px;
          font-family: inherit;
          font-size: 0.72rem;
          font-weight: 700;
          padding: 0.15rem 0.4rem;
          min-height: 1.5rem;
          cursor: pointer;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .inventory-manager .finder-multivendor-btn:hover,
        .inventory-manager .finder-multivendor-btn:focus-visible {
          background: #EDE4DA;
        }
        /* Package/unit price cells a multi-vendor item cannot answer with one
           editable number. Read-only, and visibly so. */
        .inventory-manager .finder-locked-cell {
          font-size: 0.72rem;
          color: #8B7B73;
          font-style: italic;
          padding: 0.15rem 0.3rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .inventory-manager .finder-table tr.finder-cost-row td {
          white-space: normal;
          background: #FBF8F5;
          border-bottom: 1px solid #EFE4DA;
          padding: 0.6rem 0.75rem;
        }
        /* ── Row overflow menu ("⋯") ─────────────────────────────────── */
        .inventory-manager .finder-actions-inner {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.1rem;
        }
        .inventory-manager .finder-kebab-btn {
          width: 30px;
          height: 30px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #E0D4C6;
          border-radius: 8px;
          background: #fff;
          color: #6a5c53;
          font-size: 1rem;
          line-height: 1;
          padding: 0;
          cursor: pointer;
        }
        .inventory-manager .finder-kebab-btn:hover { background: #F5F0EB; color: #493b33; }
        .inventory-manager .finder-kebab-btn:focus-visible {
          outline: 2px solid #8A5A00;
          outline-offset: 1px;
        }
        .inventory-manager .finder-kebab-btn[aria-expanded="true"] {
          background: #EDE4DA;
          border-color: #C9B9A6;
        }
        /* The menu itself portals to document.body, so it sits outside
           .inventory-manager and cannot be scoped under it. */
        .finder-kebab-menu {
          background: #fff;
          border: 1px solid #E8DDD0;
          border-radius: 10px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          z-index: 2100;
          padding: 4px 0;
          overflow: hidden;
        }
        .finder-kebab-item {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 9px 14px;
          border: none;
          background: none;
          text-align: left;
          font-family: inherit;
          font-size: 0.82rem;
          color: #5A4A42;
          cursor: pointer;
        }
        .finder-kebab-item:hover:not(:disabled),
        .finder-kebab-item:focus-visible:not(:disabled) {
          background: #FAF6F2;
          outline: none;
        }
        .finder-kebab-item:disabled { color: #C4B5A8; cursor: default; }
        .finder-kebab-item-danger { color: #8D3745; }
        .finder-kebab-icon { width: 18px; text-align: center; flex: 0 0 auto; }
        .finder-kebab-label { min-width: 0; }

        /* ── Result count + hydration status ─────────────────────────── */
        .inventory-manager .finder-summary-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.4rem;
        }
        .inventory-manager .finder-hydrating {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.78rem;
          color: #8B7B73;
        }
        .inventory-manager .finder-hydrating::before {
          content: '';
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #C9A227;
          animation: finder-hydrate-pulse 1.2s ease-in-out infinite;
        }
        @keyframes finder-hydrate-pulse {
          0%, 100% { opacity: 0.25; }
          50% { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .inventory-manager .finder-hydrating::before { animation: none; opacity: 0.7; }
        }
        .inventory-manager .finder-retry-link {
          border: 0;
          background: none;
          padding: 0;
          font: inherit;
          font-size: 0.78rem;
          color: #8D3745;
          text-decoration: underline;
          cursor: pointer;
        }
        .inventory-manager .finder-load-more {
          width: 100%;
          min-height: 44px;
          margin-top: 0.6rem;
        }
        .inventory-manager .finder-load-more-count {
          color: #9a887b;
          font-size: 0.8rem;
        }

        /* ── Phone item cards ────────────────────────────────────────── */
        .inventory-manager .finder-mobile-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .inventory-manager .finder-mobile-card {
          background: #FEFCFA;
          border: 1.5px solid #E8DDD0;
          border-radius: 12px;
          overflow: hidden;
        }
        .inventory-manager .finder-mobile-card-selected {
          border-color: #C9A227;
          box-shadow: 0 0 0 2px rgba(201, 162, 39, 0.18);
        }
        /* Card head: name and its two meta lines on the left, the price on a
           right rail, the kebab last. Grid rather than flex so the price stays
           vertically centred against a name that may run to two lines. */
        .inventory-manager .finder-mobile-head {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto auto;
          grid-template-areas:
            "name  price kebab"
            "meta  price kebab";
          align-items: start;
          gap: 0 0.5rem;
          padding: 0.6rem 0.4rem 0.6rem 0.8rem;
          cursor: pointer;
          min-height: 56px;
        }
        .inventory-manager .finder-mobile-head:focus-visible {
          outline: 2px solid #8A5A00;
          outline-offset: -2px;
        }
        .inventory-manager .finder-mobile-name {
          grid-area: name;
          font-size: 0.95rem;
          font-weight: 700;
          color: #3E2723;
          line-height: 1.25;
          /* Two lines is enough to tell two similar items apart; a third pushes
             the next card off a phone screen. */
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .inventory-manager .finder-mobile-meta {
          grid-area: meta;
          margin-top: 0.2rem;
          min-width: 0;
        }
        .inventory-manager .finder-mobile-meta-line {
          font-size: 0.75rem;
          color: #8B7B73;
          line-height: 1.35;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .inventory-manager .finder-mobile-meta-line strong {
          font-weight: 600;
          color: #6a5c53;
        }
        .inventory-manager .finder-mobile-multivendor {
          color: #8A5A00;
          font-weight: 600;
        }
        .inventory-manager .finder-mobile-price {
          grid-area: price;
          align-self: center;
          text-align: right;
          white-space: nowrap;
          font-variant-numeric: tabular-nums;
        }
        .inventory-manager .finder-mobile-price-amount {
          display: block;
          font-size: 0.95rem;
          font-weight: 700;
          color: #3E2723;
        }
        .inventory-manager .finder-mobile-price-unit {
          display: block;
          font-size: 0.68rem;
          color: #8B7B73;
          margin-top: 0.05rem;
        }
        .inventory-manager .finder-mobile-kebab {
          grid-area: kebab;
          align-self: center;
        }
        .inventory-manager .finder-mobile-kebab .finder-kebab-btn {
          width: 36px;
          height: 36px;
          font-size: 1.05rem;
        }
        /* Revealed fields are labelled pairs, two per row — a definition list,
           not a cramped continuation of the grid, so nothing truncates. */
        .inventory-manager .finder-mobile-detail {
          border-top: 1px solid #F1EAE2;
          background: #FBF8F5;
          padding: 0.7rem 0.8rem;
        }
        .inventory-manager .finder-mobile-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 0.55rem 0.7rem;
          margin: 0;
        }
        .inventory-manager .finder-mobile-cell { min-width: 0; }
        .inventory-manager .finder-mobile-cell dt {
          font-size: 0.62rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #9a887b;
          margin: 0 0 0.12rem;
        }
        .inventory-manager .finder-mobile-cell dd {
          margin: 0;
          font-size: 0.82rem;
          color: #4a3c34;
          overflow-wrap: anywhere;
        }
        .inventory-manager .finder-mobile-cell dd.empty { color: #C4B5A8; }
        .inventory-manager .finder-mobile-summary {
          margin-top: 0.75rem;
        }
        .inventory-manager .finder-mobile-edit {
          border-top: 1px solid #F1EAE2;
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
        }
        .inventory-manager .finder-mobile-field {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }
        .inventory-manager .finder-mobile-field > span {
          font-size: 0.66rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #8B7B73;
        }
        .inventory-manager .finder-mobile-field input,
        .inventory-manager .finder-mobile-field select {
          min-height: 40px;
        }
        .inventory-manager .finder-mobile-actions {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.2rem;
        }
        .inventory-manager .finder-mobile-actions .btn {
          min-height: 40px;
        }
        .inventory-manager .finder-mobile-select {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.8rem;
          color: #5A4A42;
          margin: 0;
          min-height: 40px;
        }
        .inventory-manager .finder-table td.finder-select-cell,
        .inventory-manager .finder-table th.finder-select-cell {
          text-align: center;
          padding-left: 0.15rem;
          padding-right: 0.15rem;
        }
        .inventory-manager .finder-select-cell input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
          margin: 0;
        }
        .inventory-manager .finder-selection-bar {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.6rem;
          background: #FDECEE;
          border: 1px solid #F3C7CD;
          border-radius: 10px;
          padding: 0.5rem 0.75rem;
          margin-bottom: 0.75rem;
        }
        .inventory-manager .finder-selection-bar .selection-count {
          font-weight: 700;
          color: #8D3745;
          font-size: 0.9rem;
        }
        .inventory-manager .finder-selection-bar .selection-spacer { flex: 1 1 auto; }
        .inventory-manager .finder-table tbody tr.finder-row-selected td {
          background-color: #FDECEE !important;
        }
        .inventory-manager .finder-table tbody tr:nth-child(even) {
          background-color: #FAF6F2;
        }
        .inventory-manager .finder-table tbody tr:nth-child(even) td {
          border-bottom-color: #e7ddd4;
        }
        .inventory-manager .finder-action-btn {
          font-size: 0.65rem;
          padding: 0.15rem 0.35rem;
          white-space: nowrap;
        }
        .inventory-manager .finder-controls {
          display: grid;
          grid-template-columns: minmax(220px, 2fr) minmax(140px, 1fr) minmax(140px, 1fr) minmax(140px, 1fr) minmax(120px, 1fr) auto auto;
          gap: 0.65rem;
          align-items: end;
          margin-bottom: 0.85rem;
        }
        .inventory-manager .finder-summary {
          font-size: 0.85rem;
          color: #7b6d63;
        }
        @media (max-width: 992px) {
          .inventory-manager .finder-controls {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 576px) {
          .inventory-manager .finder-controls > div {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          .inventory-manager .finder-controls > div .form-label {
            white-space: nowrap;
            margin-bottom: 0;
            flex-shrink: 0;
            font-size: 0.8rem;
            min-width: 4.5rem;
          }
          .inventory-manager .finder-controls > div .form-control,
          .inventory-manager .finder-controls > div .form-select {
            min-width: 0;
            flex: 1;
          }
          .inventory-manager #add-items-card {
            margin-bottom: 0.5rem !important;
          }
          .inventory-manager #add-items-card .card-body {
            padding: 0.75rem 1rem;
          }
          .inventory-manager #price-trends-panel {
            margin-bottom: 0.5rem !important;
          }
          .inventory-manager #price-trends-panel .card-body {
            padding: 0.75rem 1rem;
          }
          .inventory-manager #item-search-edit {
            margin-bottom: 0.5rem !important;
          }
          .inventory-manager #item-search-edit .card-body {
            padding: 0.75rem 1rem;
          }
          .inventory-manager .import-toolbar {
            display: flex;
            flex-wrap: nowrap;
            gap: 6px;
          }
          .inventory-manager .import-option {
            flex: 1 1 0;
            justify-content: center;
            font-size: 0.7rem;
            padding: 0.38rem 0.3rem;
            min-height: 38px;
            white-space: normal;
            text-align: center;
            line-height: 1.15;
          }
          .inventory-manager .import-option .import-option-icon {
            font-size: 0.85rem;
          }
          .import-tool-modal-overlay {
            align-items: flex-end;
            padding: 0;
          }
          .import-tool-modal {
            width: 100%;
            max-height: 90vh;
            border-radius: 16px 16px 0 0;
          }
        }
      `}</style>

      <div className="card border-0 shadow-sm mb-4" id="add-items-card">
        <div className="card-body p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
            <div>
              <h5 className="mb-1" style={{ color: '#5A4A42', fontWeight: 700 }}>Add your items</h5>
              <p className="text-muted mb-0">Pick a setup-style import path, then review and edit.</p>
            </div>
          </div>

          <div className="flow-help">
            Choose an import method to open tools in a modal. Manual entry keeps focus in the table below.
          </div>

          <div className="import-toolbar" role="tablist" aria-label="Add items flow">
            <button
              type="button"
              className={`import-option ${entryMode === 'import' ? 'active' : ''}`}
              onClick={() => openImportTool('import')}
            >
              <span className="import-option-icon" aria-hidden="true">📄</span>
              Import file or photo
            </button>
            <button
              type="button"
              className={`import-option ${entryMode === 'menu' ? 'active' : ''}`}
              onClick={() => openImportTool('menu')}
            >
              <span className="import-option-icon" aria-hidden="true">💡</span>
              Build from menus
            </button>
            <button
              type="button"
              className={`import-option ${entryMode === 'edit' ? 'active' : ''}`}
              onClick={() => openImportTool('edit')}
            >
              <span className="import-option-icon" aria-hidden="true">📝</span>
              Manual entry
            </button>
          </div>
          {currentStore?.id && (
            <div style={{ marginTop: '0.6rem' }}>
              <EmailImportButton
                storeId={currentStore.id}
                userEmail={(() => {
                  try {
                    return JSON.parse(localStorage.getItem('currentUser') || '{}')?.email || '';
                  } catch (_) { return ''; }
                })()}
                tone="amber"
              />
            </div>
          )}


          {isImportToolModalOpen && createPortal(
            <div
              className="import-tool-modal-overlay"
              role="presentation"
              ref={importModalOverlayRef}
              onMouseDown={(e) => {
                mouseDownOnOverlayRef.current = (e.target === importModalOverlayRef.current);
              }}
              onMouseUp={(e) => {
                if (mouseDownOnOverlayRef.current && e.target === importModalOverlayRef.current) {
                  closeImportToolModal();
                }
                mouseDownOnOverlayRef.current = false;
              }}
            >
              <div
                className="import-tool-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Import tools"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="import-tool-modal-header">
                  <h6>
                    {entryMode === 'menu' ? 'Build from menus' : 'Import file or photo'}
                  </h6>
                  <button
                    type="button"
                    className="import-tool-modal-close"
                    onClick={closeImportToolModal}
                    aria-label="Close import tool"
                  >
                    ×
                  </button>
                </div>
                <div className="import-tool-modal-body">
                  {entryMode === 'menu' ? (
                    <MenuStarterMount
                      currentStore={currentStore}
                      onCompleted={(workflow, info) => {
                        fetchInventories(currentStore?.id);
                        closeImportToolModal();
                        const ids = info && Array.isArray(info.approvedItemIds) ? info.approvedItemIds : [];
                        if (ids.length > 0) offerTemplateCreation(ids);
                      }}
                    />
                  ) : (
                    <FileUpload
                      onUpload={handleFileUpload}
                      onCancel={closeImportToolModal}
                      currentStore={currentStore}
                      uploadMode="import"
                    />
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3" id="price-trends-panel">
        <div className="card-body p-4">
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
            <div>
              <h5 className="mb-1" style={{ color: '#5A4A42', fontWeight: 700 }}>
                Price trends
                <HelpInfoButton helpTopicId="catalog.price_trends" page="catalog" section="price_trends" />
              </h5>
              <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                Biggest movers across your catalog over a chosen window. Net % is computed from the earliest old price to the latest new price recorded in the window.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setTrendsPanelOpen((v) => !v)}
              aria-expanded={trendsPanelOpen}
              aria-controls="price-trends-body"
            >
              {trendsPanelOpen ? 'Hide' : 'Show trends'}
            </button>
          </div>

          {trendsPanelOpen && (
            <div id="price-trends-body" className="mt-3">
              <div className="finder-controls">
                <div>
                  <label htmlFor="trends-window" className="form-label mb-1" style={{ color: '#6f6158', fontWeight: 600 }}>
                    Time window
                  </label>
                  <select
                    id="trends-window"
                    className="form-select"
                    value={trendsWindow}
                    onChange={(e) => setTrendsWindow(parseInt(e.target.value, 10))}
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="trends-vendor" className="form-label mb-1" style={{ color: '#6f6158', fontWeight: 600 }}>
                    Vendor
                  </label>
                  <select
                    id="trends-vendor"
                    className="form-select"
                    value={trendsVendor}
                    onChange={(e) => setTrendsVendor(e.target.value)}
                  >
                    <option value="all">All vendors</option>
                    {trendVendorOptions.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="trends-category" className="form-label mb-1" style={{ color: '#6f6158', fontWeight: 600 }}>
                    Category
                  </label>
                  <select
                    id="trends-category"
                    className="form-select"
                    value={trendsCategory}
                    onChange={(e) => setTrendsCategory(e.target.value)}
                  >
                    <option value="all">All categories</option>
                    {trendCategoryOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="trends-direction" className="form-label mb-1" style={{ color: '#6f6158', fontWeight: 600 }}>
                    Direction
                  </label>
                  <select
                    id="trends-direction"
                    className="form-select"
                    value={trendsDirection}
                    onChange={(e) => setTrendsDirection(e.target.value)}
                  >
                    <option value="all">All changes</option>
                    <option value="increases">Increases only</option>
                    <option value="decreases">Decreases only</option>
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => currentStore?.id && loadPriceTrends(currentStore.id, trendsWindow)}
                  disabled={trendsLoading || !currentStore?.id}
                >
                  {trendsLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>

              <div className="finder-summary mb-2" style={{ marginTop: '0.5rem' }}>
                {trendsLoading
                  ? 'Loading price trends...'
                  : `${trendRows.length} item${trendRows.length === 1 ? '' : 's'} shown (top movers by % change)`}
                {trendsLoadedAt && !trendsLoading && (
                  <span className="text-muted" style={{ marginLeft: '0.5rem' }}>
                    • Updated {trendsLoadedAt.toLocaleTimeString()}
                  </span>
                )}
              </div>
              {trendsRawRows.length >= 5000 && !trendsLoading && (
                <div className="alert alert-warning py-2 mb-2" style={{ fontSize: '0.8rem' }}>
                  Showing the most recent 5,000 price changes in this window. Narrow the time range for fully accurate top-mover rankings on very busy stores.
                </div>
              )}

              {trendsError ? (
                <div className="alert alert-danger py-2 mb-0" style={{ fontSize: '0.85rem' }}>
                  {trendsError}
                </div>
              ) : trendsLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : trendRows.length === 0 ? (
                <div className="alert alert-light border mb-0" style={{ borderColor: '#e7ddd4', color: '#6f6259' }}>
                  No price changes recorded for the selected window and filters.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="table table-sm align-middle" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: '#F5F3F0' }}>
                        <th style={{ color: '#5A4A42', fontWeight: 600 }}>Item</th>
                        <th style={{ color: '#5A4A42', fontWeight: 600 }}>Vendor</th>
                        <th style={{ color: '#5A4A42', fontWeight: 600 }}>Category</th>
                        <th style={{ color: '#5A4A42', fontWeight: 600 }}>Basis</th>
                        <th style={{ color: '#5A4A42', fontWeight: 600 }}>Old → New</th>
                        <th style={{ color: '#5A4A42', fontWeight: 600 }}>Δ ($)</th>
                        <th style={{ color: '#5A4A42', fontWeight: 600 }}>Δ (%)</th>
                        <th style={{ color: '#5A4A42', fontWeight: 600 }}>Changes</th>
                        <th style={{ color: '#5A4A42', fontWeight: 600 }}>Last change</th>
                        <th style={{ color: '#5A4A42', fontWeight: 600 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {trendRows.map((row) => (
                        <tr key={row.itemId}>
                          <td style={{ fontWeight: 600, color: '#3a2e28' }}>{row.description}</td>
                          <td>{row.vendor}</td>
                          <td>{row.category}</td>
                          <td className="text-muted text-uppercase" style={{ fontSize: '0.75rem' }}>{row.basis}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {formatPrice(row.oldPrice)} → {formatPrice(row.newPrice)}
                          </td>
                          <td style={{ color: deltaColor(row.deltaAbs), fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {formatDeltaAbsolute(row.deltaAbs)}
                          </td>
                          <td style={{ color: deltaColor(row.deltaPct), fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {formatDeltaPct(row.deltaPct)}
                          </td>
                          <td>{row.changeCount}</td>
                          <td className="text-muted small" style={{ whiteSpace: 'nowrap' }}>
                            {formatHistoryTimestamp(row.lastChangedAt)}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-info"
                              onClick={() => openPriceHistory(inventoryById[row.itemId])}
                              title="Open price change history"
                            >
                              History
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm" id="item-search-edit">
        <div className="card-body p-4">
          <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-2">
            <div>
              <h5 className="mb-1" style={{ color: '#5A4A42', fontWeight: 700 }}>
                Catalog
                <HelpInfoButton helpTopicId="catalog.overview" page="catalog" section="catalog_table" />
              </h5>
              <p className="text-muted mb-0">
                {catalogView === 'items'
                  ? 'Search, sort, and edit one inventory item per row.'
                  : 'Scan, edit, accept, or create product relationships.'}
              </p>
            </div>
            <small className="text-muted">
              {/* These are whole-catalog claims, so they wait for the whole
                  catalog. Reporting the first page's 100 as "100 items" for a
                  1,284-item store is a wrong number stated confidently — worse
                  than saying it is still counting. */}
              {catalogView === 'related'
                ? ''
                : loading || !firstLoadDone
                ? 'Loading items...'
                : !hydration.complete
                  ? `Counting… ${hydration.loaded.toLocaleString()} items so far`
                  : `${stats.totalItems} items • ${stats.categories} categories • ${stats.areas} areas • ${stats.vendors} vendors`}
            </small>
          </div>

          <div className="btn-group mb-3" role="tablist" aria-label="Catalog view">
            <button
              type="button"
              role="tab"
              aria-selected={catalogView === 'items'}
              className={`btn btn-sm ${catalogView === 'items' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setCatalogView('items')}
            >
              Catalog view
            </button>
            <HelpInfoButton helpTopicId="catalog.inventory_items" page="catalog" section="catalog_tabs"
              context={{ active_catalog_tab: 'inventory_items' }} style={{ alignSelf: 'center', marginLeft: '4px' }} />
            <button
              type="button"
              role="tab"
              aria-selected={catalogView === 'related'}
              className={`btn btn-sm ${catalogView === 'related' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => {
                // Always land on the Related Products tab itself — never the
                // Subs & Alts workspace. The browser/session retaining the
                // subs placement is intentional only for the Back action
                // *inside* that workspace (onBackToProducts).
                setRelatedSubView('products');
                setCatalogView('related');
                if (currentStore?.id && inventories.length === 0) fetchInventories(currentStore.id);
              }}
            >
              Related products
            </button>
            <HelpInfoButton helpTopicId="catalog.related_products" page="catalog" section="catalog_tabs"
              context={{ active_catalog_tab: 'related_products' }} style={{ alignSelf: 'center', marginLeft: '4px' }} />
          </div>


          {catalogView === 'items' && <div className="finder-controls">
            <div>
              <label htmlFor="inventory-search" className="form-label mb-1" style={{ color: '#6f6158', fontWeight: 600 }}>
                Search items
              </label>
              <input
                id="inventory-search"
                type="search"
                className="form-control"
                value={finderQuery}
                onChange={(event) => setFinderQuery(event.target.value)}
                placeholder="Search by name, vendor, category, area, brand, or code"
              />
            </div>
            <div>
              <label htmlFor="inventory-vendor-filter" className="form-label mb-1" style={{ color: '#6f6158', fontWeight: 600 }}>
                Vendor
              </label>
              <select
                id="inventory-vendor-filter"
                className="form-select"
                value={finderVendor}
                onChange={(event) => setFinderVendor(event.target.value)}
              >
                <option value="all">All vendors</option>
                {finderVendorOptions.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="inventory-category-filter" className="form-label mb-1" style={{ color: '#6f6158', fontWeight: 600 }}>
                Category
              </label>
              <select
                id="inventory-category-filter"
                className="form-select"
                value={finderCategory}
                onChange={(event) => setFinderCategory(event.target.value)}
              >
                <option value="all">All categories</option>
                {finderCategoryOptions.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            {catalogView === 'items' && <>
              <div>
                <label htmlFor="inventory-sort-by" className="form-label mb-1" style={{ color: '#6f6158', fontWeight: 600 }}>
                  Sort by
                </label>
                <select
                  id="inventory-sort-by"
                  className="form-select"
                  value={sortField}
                  onChange={(event) => setSortField(event.target.value)}
                >
                  {SORTABLE_COLUMNS.map((column) => (
                    <option key={column.key} value={column.key}>{column.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="inventory-sort-order" className="form-label mb-1" style={{ color: '#6f6158', fontWeight: 600 }}>
                  Order
                </label>
                <select
                  id="inventory-sort-order"
                  className="form-select"
                  value={sortDirection}
                  onChange={(event) => setSortDirection(event.target.value)}
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveAllDirtyRows}
                disabled={savingItem}
              >
                {savingItem ? 'Saving...' : `Save changes (${dirtyItemIds.length})`}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={downloadFinderCsv}
                /* A CSV built while the catalog is still streaming in would be
                   a silently short file. Better to make the user wait a moment
                   than to hand them an export that quietly omits rows. */
                disabled={finderResults.length === 0 || downloadingCsv || !hydration.complete}
                title={hydration.complete
                  ? 'Download one raw row per vendor product'
                  : 'Still loading the full catalog — the download will be complete in a moment'}
              >
                {downloadingCsv ? 'Preparing CSV…' : '↓ Download'}
              </button>
              <button
                type="button"
                className={`btn ${catalogGrouped ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setCatalogGrouped((v) => !v)}
                title="See related products grouped or all products independent of relationships"
                data-testid="catalog-grouped-toggle"
                aria-pressed={catalogGrouped}
              >
                {catalogGrouped ? 'View: Related grouped' : 'View: All independent'}
              </button>
            </>}
          </div>}

          {catalogView === 'items' && <div className="finder-summary mb-2 finder-summary-bar">
            <span>
              {/* Say what is on screen AND what it is a slice of. "40 rows
                  shown" alone reads as "your store has 40 items". */}
              {visibleResults.length < finderResults.length
                ? <>Showing <strong>{visibleResults.length}</strong> of {finderResults.length.toLocaleString()} matching item{finderResults.length === 1 ? '' : 's'}</>
                : <>{finderResults.length.toLocaleString()} item{finderResults.length === 1 ? '' : 's'}</>}
              {dirtyItemIds.length > 0 ? ` • ${dirtyItemIds.length} unsaved` : ''}
            </span>
            {remoteSearching ? (
              <span className="finder-hydrating">Searching the full catalog…</span>
            ) : !hydration.complete && !hydration.failed ? (
              <span className="finder-hydrating">
                Loading the rest ({hydration.loaded.toLocaleString()} so far) — search covers everything
              </span>
            ) : hydration.failed ? (
              <button type="button" className="finder-retry-link" onClick={() => currentStore?.id && fetchInventories(currentStore.id)}>
                Some items didn’t load — retry
              </button>
            ) : null}
          </div>}
          {saveNotice && (
            <div className={`alert alert-${saveNotice.type} py-2`} role="alert">
              {saveNotice.message}
            </div>
          )}

          {catalogView === 'items' && selectedCount > 0 && (
            <div className="finder-selection-bar">
              <span className="selection-count">
                {selectedCount} item{selectedCount === 1 ? '' : 's'} selected
              </span>
              {selectedCount - selectedVisibleCount > 0 && (
                <span style={{ fontSize: '0.8rem', color: '#8D3745' }}>
                  ({selectedCount - selectedVisibleCount} not shown by the current search/filter)
                </span>
              )}
              <span className="selection-spacer" />
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={clearSelection}
                disabled={deleting}
              >
                Clear selection
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={openDeleteModal}
                disabled={deleting}
              >
                🗑 Delete selected
              </button>
            </div>
          )}

          {catalogView === 'related' && (
            // A document import's related-product classification drains for
            // ~20–30 minutes AFTER the import review closes. Without this
            // banner a user opening this view mid-drain read the partial set
            // as "matching didn't work". Its polls also revive a stalled
            // drain server-side, and settling refreshes the confirmed and
            // suggested relationship states without a reload.
            <ImportEnrichmentStatusBanner
              storeId={currentStore?.id}
              onSettled={() => {
                if (!currentStore?.id) return;
                refreshItemRelationships(currentStore.id);
              }}
            />
          )}
          {catalogView === 'related' ? (
            // Shimmer ONLY while there is nothing to show. `loading` is
            // toggled by every fetchInventories, including the one this view's
            // own onChanged triggers — gating on it unmounted the workspace
            // after each accept, which threw the user off the suggestions
            // surface back to the relationships list and discarded the
            // "Relationship accepted" notice they were meant to read.
            (loading || !firstLoadDone) && inventories.length === 0 ? (
              <ShimmerLoader compact />
            ) : relatedSubView === 'products' ? (
              // The catalog's own related products: one item, several vendor
              // products. Editing opens the SAME modal the item rows open.
              <RelatedProductGroupsView
                items={inventories}
                optionsByItemId={optionsByItemId}
                vendorNameById={vendorNameById}
                storedCanonicalByGroupKey={storedCanonicalByGroupKey}
                // comparison_unit is not in INVENTORY_BASE_COLUMNS, so it does
                // not ride along on `inventories`. It is already in state from
                // the same loadPurchaseOptions call ManageRelationshipsView
                // reads — no extra fetch, just the prop.
                comparisonItemById={comparisonItemById}
                // The confirmed EQUIVALENT graph + stored canonicals: the view
                // folds sibling items into one card per group with these.
                itemRelationships={itemRelationships}
                groupComparisonByKey={groupComparisonByKey}
                // Real purchasing history behind "Last Used" — never is_preferred.
                orderUseByOptionId={orderUseByOptionId}
                // What the substitutes workspace will actually SHOW. Confirmed
                // equivalences are Related Products and are rendered right
                // here; counting them on the link that leads away from this
                // screen promised a workspace full of rows that are not there.
                substituteCount={secondaryRelationshipMetrics(itemRelationships).confirmedRelationshipCount}
                // Editing a group writes relationships and purchase options,
                // then asks the server to recompute the group's comparison.
                storeId={currentStore?.id}
                onChanged={async () => {
                  if (!currentStore?.id) return;
                  // A group edit can reject a same_item link, split a vendor
                  // product onto a new standalone item, or add a member, so
                  // the graph, the catalog and the stored group comparisons
                  // all have to be re-read.
                  await Promise.all([
                    refreshItemRelationships(currentStore.id),
                    fetchInventories(currentStore.id),
                  ]);
                }}
                onEditItem={(item, { reviewComparison = false, comparisonUnit = '' } = {}) => {
                  const resolved = inventoryById[item.id] || item;
                  setPurchaseOptionsItem(reviewComparison
                    ? {
                        ...resolved,
                        __openComparisonReview: true,
                        __comparisonReviewUnit: comparisonUnit,
                      }
                    : resolved);
                }}
                onOpenSubstitutes={() => setRelatedSubView('substitutes')}
              />
            ) : (
              <ManageRelationshipsView
                onBackToProducts={() => setRelatedSubView('products')}
                storeId={currentStore?.id}
                items={inventories}
                relationships={itemRelationships}
                optionsByItemId={optionsByItemId}
                comparisonItemById={comparisonItemById}
                vendorNameById={vendorNameById}
                onChanged={async () => {
                  if (!currentStore?.id) return;
                  // Relationship edits can split one vendor option into a new
                  // standalone item, so refresh the catalog/options as well as
                  // the relationship graph.
                  await Promise.all([
                    refreshItemRelationships(currentStore.id),
                    fetchInventories(currentStore.id),
                  ]);
                }}
              />
            )
          ) : loading || !firstLoadDone ? (
            <ShimmerLoader compact />
          ) : finderResults.length === 0 ? (
            <div className="alert alert-light border mb-0" style={{ borderColor: '#e7ddd4', color: '#6f6259' }}>
              {deferredFinderQuery.trim() || finderVendor !== 'all' || finderCategory !== 'all'
                ? (remoteSearching ? 'Searching the full catalog…' : 'No items match your current search.')
                : 'Your catalog is empty. Add items above, then search and edit here.'}
            </div>
          ) : isMobileViewport ? (
            /* Phones get stacked cards rather than a 16-column table turned
               sideways. The collapsed card carries exactly six things — name,
               vendor, category, area, unit and price — because those are what
               someone standing in a walk-in is actually looking for. Everything
               else is one tap away, laid out as labelled pairs rather than a
               cramped continuation of the grid. */
            <div className="finder-mobile-list">
              {visibleResults.map((row) => {
                const isDirty = dirtyItemIds.includes(row.id);
                const isSelected = selectedItemIds.has(row.id);
                const summary = costSummaryByItemId[row.id] || null;
                const isOpen = expandedCostItemId === row.id;
                const priceText = describeRowPrice(row, summary);
                const vendorText = describeRowVendor(row, summary);
                const detailFields = mobileDetailFields(row);
                return (
                  <div key={row.id} className={`finder-mobile-card${isSelected ? ' finder-mobile-card-selected' : ''}`}>
                    <div
                      className="finder-mobile-head"
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      aria-label={`${row.description || `Item ${row.id}`} — show details`}
                      onClick={() => setExpandedCostItemId(isOpen ? null : row.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setExpandedCostItemId(isOpen ? null : row.id);
                        }
                      }}
                    >
                      {/* Tap the name to see what each vendor in this row's
                          pool actually calls it, and charges for it. */}
                      <GroupMemberNamesTooltip
                        options={pooledOptionsByItemId[row.id] || []}
                        vendorNameById={vendorNameById}
                        itemName={row.description}
                        mobile
                      >
                        <div className="finder-mobile-name">{row.description || `Item ${row.id}`}</div>
                      </GroupMemberNamesTooltip>
                      <div className="finder-mobile-meta">
                        <div className="finder-mobile-meta-line">
                          <span className={vendorText.isMulti ? 'finder-mobile-multivendor' : undefined}>{vendorText.label}</span>
                          {row.category ? <> · {row.category}</> : null}
                        </div>
                        <div className="finder-mobile-meta-line">
                          {row.location || 'No area'}{row.unit ? <> · per <strong>{row.unit}</strong></> : null}
                        </div>
                      </div>
                      <div className="finder-mobile-price">
                        <span className="finder-mobile-price-amount">{priceText.amount}</span>
                        {priceText.suffix && <span className="finder-mobile-price-unit">{priceText.suffix}</span>}
                      </div>
                      <div className="finder-mobile-kebab" onClick={(event) => event.stopPropagation()}>
                        <RowActionMenu
                          label={`Actions for ${row.description || `item ${row.id}`}`}
                          items={rowActionItems(row, { isDirty, isSelected })}
                        />
                      </div>
                    </div>

                    {isOpen && (
                      <div className="finder-mobile-detail">
                        <dl className="finder-mobile-grid">
                          {detailFields.map((field) => (
                            <div key={field.label} className="finder-mobile-cell">
                              <dt>{field.label}</dt>
                              <dd className={field.value ? undefined : 'empty'}>{field.value || '—'}</dd>
                            </div>
                          ))}
                        </dl>

                        {summary && (
                          <div className="finder-mobile-summary">
                            <ItemCostSummaryCell
                              summary={summary}
                              layout="card"
                              loading={optionsLoading}
                              onViewOptions={() => setPurchaseOptionsItem(itemLookupById[row.id] || row)}
                            />
                          </div>
                        )}

                        <div className="finder-mobile-edit">
                          {MOBILE_EDIT_FIELDS.map((field) => (
                            <label key={field.key} className="finder-mobile-field">
                              <span>{field.label}</span>
                              {field.type === 'select' ? (
                                <select
                                  className="form-select form-select-sm"
                                  value={row[field.key] ?? ''}
                                  onChange={(event) => updateRowField(row.id, field.key, event.target.value)}
                                >
                                  {(field.options || []).map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type={field.type || 'text'}
                                  step={field.type === 'number' ? '0.01' : undefined}
                                  className="form-control form-control-sm"
                                  value={row[field.key] ?? ''}
                                  onChange={(event) => updateRowField(row.id, field.key, event.target.value)}
                                />
                              )}
                            </label>
                          ))}
                          <div className="finder-mobile-actions">
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={savingItem || !isDirty}
                              onClick={() => saveSingleRow(row.id)}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              disabled={savingItem || !isDirty}
                              onClick={() => discardRowChanges(row.id)}
                            >
                              Revert
                            </button>
                            <label className="finder-mobile-select">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleRowSelected(row.id)}
                              />
                              <span>Select</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {renderShowMore()}
            </div>
          ) : (
            <div className="finder-table-wrap">
              {/* Focusable and labelled: a keyboard-only user otherwise has no
                  way to scroll this container sideways at all. */}
              <div
                className="finder-table-scroll"
                role="region"
                tabIndex={0}
                aria-label="Catalog grid — scrolls horizontally"
              >
                <table className="table table-sm finder-table align-middle">
                  <colgroup>
                    <col className="col-select" />
                    <col className="col-name" />
                    <col className="col-vendor" />
                    <col className="col-category" />
                    <col className="col-taxable" />
                    <col className="col-date" />
                    <col className="col-area" />
                    <col className="col-brand" />
                    <col className="col-packsize" />
                    <col className="col-unit" />
                    <col className="col-caseqty" />
                    <col className="col-splitqty" />
                    <col className="col-caseprice" />
                    <col className="col-unitprice" />
                    <col className="col-code" />
                    <col className="col-actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="finder-select-cell">
                        <input
                          type="checkbox"
                          aria-label="Select all shown items"
                          title="Select all shown items"
                          checked={allVisibleSelected}
                          ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                          onChange={toggleSelectAllVisible}
                        />
                      </th>
                      <FinderSortableHeader field="description" label="Name" sortField={sortField} sortDirection={sortDirection} onSort={sortByHeader} />
                      <FinderSortableHeader field="vendor" label="Vendor" sortField={sortField} sortDirection={sortDirection} onSort={sortByHeader}>
                        <HelpInfoButton helpTopicId="catalog.vendor" page="catalog" section="catalog_table" />
                      </FinderSortableHeader>
                      <FinderSortableHeader field="category" label="Category" sortField={sortField} sortDirection={sortDirection} onSort={sortByHeader} />
                      <FinderSortableHeader field="taxable" label="Taxed" className="th-taxable" sortField={sortField} sortDirection={sortDirection} onSort={sortByHeader} />
                      <FinderSortableHeader field="created_at" label="Date Added" sortField={sortField} sortDirection={sortDirection} onSort={sortByHeader} />
                      <FinderSortableHeader field="location" label="Area" sortField={sortField} sortDirection={sortDirection} onSort={sortByHeader} />
                      <FinderSortableHeader field="brand" label="Brand" sortField={sortField} sortDirection={sortDirection} onSort={sortByHeader} />
                      <FinderSortableHeader field="pack_size" label="Pack/Size" sortField={sortField} sortDirection={sortDirection} onSort={sortByHeader}>
                        <HelpInfoButton helpTopicId="catalog.pack_size" page="catalog" section="catalog_table" />
                      </FinderSortableHeader>
                      <FinderSortableHeader field="unit" label="Unit" sortField={sortField} sortDirection={sortDirection} onSort={sortByHeader}>
                        <HelpInfoButton helpTopicId="catalog.unit" page="catalog" section="catalog_table" />
                      </FinderSortableHeader>
                      <FinderSortableHeader field="case_qty" label="Case Qty" sortField={sortField} sortDirection={sortDirection} onSort={sortByHeader}>
                        <HelpInfoButton helpTopicId="catalog.case_quantity" page="catalog" section="catalog_table" />
                      </FinderSortableHeader>
                      <FinderSortableHeader field="split_qty" label="Split Qty" sortField={sortField} sortDirection={sortDirection} onSort={sortByHeader}>
                        <HelpInfoButton helpTopicId="catalog.split_quantity" page="catalog" section="catalog_table" />
                      </FinderSortableHeader>
                      <FinderSortableHeader field="case_price" label="Case $" sortField={sortField} sortDirection={sortDirection} onSort={sortByHeader}>
                        <HelpInfoButton helpTopicId="catalog.case_price" page="catalog" section="catalog_table" />
                      </FinderSortableHeader>
                      <FinderSortableHeader field="unit_price" label="Unit $" sortField={sortField} sortDirection={sortDirection} onSort={sortByHeader}>
                        <HelpInfoButton helpTopicId="catalog.unit_price" page="catalog" section="catalog_table" />
                      </FinderSortableHeader>
                      <FinderSortableHeader field="item_code" label="Code" sortField={sortField} sortDirection={sortDirection} onSort={sortByHeader}>
                        <HelpInfoButton helpTopicId="catalog.item_code" page="catalog" section="catalog_table" />
                      </FinderSortableHeader>
                      {/* The heading is for screen readers only now — the
                          column is one 30px kebab wide, and "Actions" written
                          above it would set the column's minimum width to
                          something four times larger than its content. */}
                      <th className="finder-actions-cell"><span className="visually-hidden">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleResults.map((row, rowIndex) => {
                      const isDirty = dirtyItemIds.includes(row.id);
                      const isSelected = selectedItemIds.has(row.id);
                      const matchedAlias = aliasMatchByItemId.get(row.id) || null;
                      const summary = costSummaryByItemId[row.id] || null;
                      const isMultiVendor = !!summary?.isMultiVendor;
                      // A row standing in for a folded EQUIVALENT group locks
                      // its inline vendor/price cells exactly like a
                      // multi-vendor item: the group's members are managed
                      // through the relationship, never edited flat.
                      const isGroupedRow = catalogGrouped && (summary?.linkage?.groupMemberCount || 0) >= 2;
                      const lockInlineCells = isMultiVendor || isGroupedRow;
                      const vendorCellText = describeRowVendor(row, summary);
                      const isCostExpanded = expandedCostItemId === row.id;
                      return (
                        <React.Fragment key={row.id}>
                        <tr className={isSelected ? 'finder-row-selected' : undefined}>
                          <td className="finder-select-cell">
                            <input
                              type="checkbox"
                              aria-label={`Select ${row.description || `item ${row.id}`}`}
                              checked={isSelected}
                              onChange={() => toggleRowSelected(row.id)}
                            />
                          </td>
                          <td>
                            {/* Hovering the name shows what each vendor in this
                                row's pool calls it and charges — the catalog
                                itself only ever shows our one canonical name,
                                and for a folded group that is the elected
                                member's. hoverOnly: clicking must edit, not pin.
                                The native title is dropped so the browser's own
                                tooltip does not fight the panel. */}
                            <GroupMemberNamesTooltip
                              options={pooledOptionsByItemId[row.id] || []}
                              vendorNameById={vendorNameById}
                              itemName={row.description}
                              hoverOnly
                            >
                              <input
                                type="text"
                                className="form-control form-control-sm finder-input"
                                value={row.description}
                                onChange={(event) => updateRowField(row.id, 'description', event.target.value)}
                              />
                            </GroupMemberNamesTooltip>
                            {matchedAlias && (
                              <div className="finder-alias-subline" title={`Matched vendor product: ${matchedAlias}`}>
                                Matched vendor product: {matchedAlias}
                              </div>
                            )}
                          </td>
                          <td>
                            {/* A canonical item bought from several vendors has no single
                                vendor to edit here — writing one would silently pick a
                                winner. The cell becomes the way into the vendor products
                                instead, with the preferred vendor named underneath. A
                                folded group row reads "N vendors · M products" — the
                                pooled picture across its hidden sibling items. */}
                            {lockInlineCells ? (
                              <>
                                <button
                                  type="button"
                                  className="finder-multivendor-btn"
                                  aria-label={`${vendorCellText.label} for ${row.description || `item ${row.id}`} — show related products group`}
                                  onClick={() => {
                                    const group = catalogFoldedGroups.find(
                                      (g) => String(g.itemId) === String(row.id)
                                        || (Array.isArray(g.memberItemIds) && g.memberItemIds.map(String).includes(String(row.id)))
                                    );
                                    const targetKey = group ? String(group.equivalentGroupId ?? group.itemId) : String(row.id);
                                    setCatalogGroupModalKey(targetKey);
                                  }}
                                >
                                  <span aria-hidden="true">▸</span>
                                  {` ${vendorCellText.label}`}
                                </button>
                                <HelpInfoButton helpTopicId="catalog.multivendor_item" page="catalog" section="catalog_table"
                                  context={{ selected_item_id: row.id, vendor_count: summary.optionsSummary.vendorCount }} />
                                <div className="finder-alias-subline">
                                  {summary.preferred?.vendorLabel
                                    ? `Preferred: ${summary.preferred.vendorLabel}`
                                    : 'No preferred vendor'}
                                </div>
                              </>
                            ) : (
                              <input
                                type="text"
                                className="form-control form-control-sm finder-input"
                                value={row.vendor}
                                placeholder="Vendor"
                                onChange={(event) => updateRowField(row.id, 'vendor', event.target.value)}
                              />
                            )}
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm finder-input"
                              value={row.category}
                              onChange={(event) => updateRowField(row.id, 'category', event.target.value)}
                            />
                          </td>
                          <td>
                            {/* Three states to STORE, two ways to behave.
                                Blank means nobody has answered yet, which is
                                worth knowing — alcohol is deliberately left
                                blank because the answer is jurisdictional, and
                                the import/AI passes only fill rows nobody has
                                answered. But an unanswered item is NOT taxed in
                                effect: nothing may assume tax without a yes.
                                A checkbox cannot express that third state, and
                                persistItemDraft reads these exact strings. */}
                            <select
                              className="form-select form-select-sm finder-input"
                              aria-label={`Sales tax for ${row.description || `item ${row.id}`}`}
                              title="Not set counts as not taxed. It stays blank so an import or invoice can still answer it later."
                              value={row.taxable ?? ''}
                              onChange={(event) => updateRowField(row.id, 'taxable', event.target.value)}
                            >
                              {/* Y / N / blank, not words: at this column width a
                                  word is read as an ellipsized fragment, and a
                                  single letter is read at a glance down 61 rows.
                                  The title on each option carries the full
                                  meaning for hover and assistive tech. */}
                              <option value="" title="Not set — counts as not taxed"></option>
                              <option value="true" title="Taxed">Y</option>
                              <option value="false" title="Not taxed">N</option>
                            </select>
                          </td>
                          <td className="small text-muted" title={formatDateAdded(row.created_at)}>{formatDateAdded(row.created_at)}</td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm finder-input"
                              value={row.location}
                              onChange={(event) => updateRowField(row.id, 'location', event.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm finder-input"
                              value={row.brand}
                              onChange={(event) => updateRowField(row.id, 'brand', event.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm finder-input"
                              value={row.pack_size}
                              onChange={(event) => updateRowField(row.id, 'pack_size', event.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm finder-input"
                              value={row.unit}
                              onChange={(event) => updateRowField(row.id, 'unit', event.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm finder-input finder-input-num"
                              value={row.case_qty}
                              onChange={(event) => updateRowField(row.id, 'case_qty', event.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm finder-input finder-input-num"
                              value={row.split_qty}
                              onChange={(event) => updateRowField(row.id, 'split_qty', event.target.value)}
                            />
                          </td>
                          <td>
                            {/* Package prices belong to a vendor product, not to the
                                canonical item, once there is more than one vendor —
                                or more than one linked group member. */}
                            {lockInlineCells ? (
                              <div
                                className="finder-locked-cell"
                                title="Package prices differ by vendor — open the vendor products to see or change them."
                              >
                                per vendor
                              </div>
                            ) : (
                              <input
                                type="number"
                                step="0.01"
                                className="form-control form-control-sm finder-input finder-input-num"
                                value={row.case_price}
                                onChange={(event) => updateRowField(row.id, 'case_price', event.target.value)}
                              />
                            )}
                          </td>
                          <td>
                            {lockInlineCells ? (
                              <div
                                className="finder-locked-cell"
                                title={summary.currentCost.available
                                  ? `Current inventory cost — ${summary.currentCost.method.label}`
                                  : summary.currentCost.reasonText}
                              >
                                {summary.currentCost.available
                                  ? formatUnitPrice(summary.currentCost.amount, summary.countUnit)
                                  : 'unavailable'}
                              </div>
                            ) : (
                              <input
                                type="number"
                                step="0.01"
                                className="form-control form-control-sm finder-input finder-input-num"
                                value={row.unit_price}
                                onChange={(event) => updateRowField(row.id, 'unit_price', event.target.value)}
                              />
                            )}
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm finder-input"
                              value={row.item_code}
                              onChange={(event) => updateRowField(row.id, 'item_code', event.target.value)}
                            />
                          </td>
                          {/* Four labelled buttons used to live here in an 11%
                              column — which still could not fit all four, and
                              which cost more horizontal room than Case $ and
                              Unit $ combined. They are all still here, one tap
                              behind a kebab. */}
                          <td className="finder-actions-cell">
                            <div className="finder-actions-inner">
                              <RowActionMenu
                                label={`Actions for ${row.description || `item ${row.id}`}`}
                                items={rowActionItems(row, { isDirty, isSelected })}
                              />
                              {/* One shared History help trigger — on the first
                                  visible row's action only, so dense rows don't
                                  each grow an icon. */}
                              {rowIndex === 0 && (
                                <HelpInfoButton helpTopicId="catalog.item_history" page="catalog" section="catalog_table"
                                  context={{ selected_item_id: row.id }} />
                              )}
                            </div>
                          </td>
                        </tr>
                        {isCostExpanded && summary && (
                          <tr className="finder-cost-row">
                            <td colSpan={16}>
                              <ItemCostSummaryCell
                                summary={summary}
                                layout="row"
                                loading={optionsLoading}
                                onViewOptions={() => {
                                  setPurchaseOptionsItem(itemLookupById[row.id] || row);
                                }}
                              />
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {renderShowMore()}
            </div>
          )}
        </div>
      </div>

      {aliasPrompt && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '1.5rem',
            maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <h5 style={{ marginBottom: '0.75rem', color: '#3a2e28' }}>Associate vendor names?</h5>
            <p style={{ fontSize: '0.9rem', color: '#5A4A42', marginBottom: '1rem' }}>
              You changed the vendor from <strong>{aliasPrompt.oldVendorName}</strong> to{' '}
              <strong>{aliasPrompt.newVendorName}</strong>.
            </p>
            <p style={{ fontSize: '0.85rem', color: '#6B5A52', marginBottom: '1.25rem' }}>
              Would you like to treat <em>{aliasPrompt.oldVendorName}</em> as an alias for{' '}
              <em>{aliasPrompt.newVendorName}</em>?{' '}
              <strong>All existing items</strong> with vendor <em>{aliasPrompt.oldVendorName}</em>{' '}
              will be updated to <em>{aliasPrompt.newVendorName}</em>, and future imports
              using either name will resolve to the same vendor.
            </p>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleAliasDismiss}
              >
                No, keep separate
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleAliasConfirm}
              >
                Yes, create alias
              </button>
            </div>
          </div>
        </div>
      )}

      {templateOfferIds && (
        <TemplateOfferPrompt
          itemIds={templateOfferIds}
          storeId={currentStore?.id}
          onClose={() => setTemplateOfferIds(null)}
        />
      )}

      {/* Bulk delete — two-step confirm: (1) confirm intent, (2) type "delete". */}
      {deleteModalOpen && createPortal(
        <div
          className="import-tool-modal-overlay"
          role="presentation"
          onMouseDown={(e) => { if (e.target === e.currentTarget) mouseDownOnOverlayRef.current = true; }}
          onMouseUp={(e) => {
            if (mouseDownOnOverlayRef.current && e.target === e.currentTarget) closeDeleteModal();
            mouseDownOnOverlayRef.current = false;
          }}
        >
          <div
            className="import-tool-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Delete selected items"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ maxWidth: '520px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="import-tool-modal-header">
              <h6 style={{ margin: 0, color: '#8D3745' }}>
                {deleteStage === 'confirm'
                  ? `Delete ${selectedCount} item${selectedCount === 1 ? '' : 's'}?`
                  : 'Type “delete” to confirm'}
              </h6>
              <button
                type="button"
                className="import-tool-modal-close"
                onClick={closeDeleteModal}
                aria-label="Close"
                disabled={deleting}
              >×</button>
            </div>
            <div className="import-tool-modal-body" style={{ overflowY: 'auto', flex: 1, padding: '1rem' }}>
              {deleteStage === 'confirm' ? (
                <>
                  <p style={{ fontSize: '0.9rem', color: '#5A4A42', marginBottom: '0.75rem' }}>
                    Confirm you want to delete {selectedCount === 1 ? 'this item' : 'these items'}. They'll be
                    removed from your catalog and dropped from future counts and orders.
                  </p>
                  <div className="alert alert-info py-2" style={{ fontSize: '0.82rem' }}>
                    This can be undone on our side — we keep a hidden copy, so contact support if you delete
                    something by mistake and need it back. Past counts, orders, and reports are never changed.
                  </div>
                  {selectedCount - selectedVisibleCount > 0 && (
                    <p style={{ fontSize: '0.82rem', color: '#8D3745', marginBottom: '0.5rem' }}>
                      Note: {selectedCount - selectedVisibleCount} of these {selectedCount - selectedVisibleCount === 1 ? 'is' : 'are'} not shown by your current search/filter — the full list is below.
                    </p>
                  )}
                  <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid #EDE8E3', borderRadius: '8px', padding: '0.4rem 0.75rem' }}>
                    {selectedItemsForDelete.slice(0, 200).map((it) => (
                      <div
                        key={it.id}
                        style={{ fontSize: '0.82rem', color: '#3a2e28', padding: '0.12rem 0', borderBottom: '1px solid #F3EEE9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={it.description}
                      >
                        {it.description}
                      </div>
                    ))}
                    {selectedItemsForDelete.length > 200 && (
                      <div style={{ fontSize: '0.8rem', color: '#8B7B73', paddingTop: '0.35rem' }}>
                        …and {selectedItemsForDelete.length - 200} more
                      </div>
                    )}
                  </div>
                  {deleteError && (
                    <div className="alert alert-danger mt-2 mb-0 py-2" style={{ fontSize: '0.82rem' }}>{deleteError}</div>
                  )}
                </>
              ) : (
                <>
                  <p style={{ fontSize: '0.9rem', color: '#5A4A42', marginBottom: '0.5rem' }}>
                    You're about to delete <strong>{selectedCount}</strong> item{selectedCount === 1 ? '' : 's'}.
                    To confirm, type the word <strong>delete</strong> below.
                  </p>
                  <input
                    type="text"
                    className="form-control"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="delete"
                    autoFocus
                    autoComplete="off"
                    aria-label="Type delete to confirm"
                    onKeyDown={(e) => { if (e.key === 'Enter' && deleteConfirmMatches && !deleting) handleConfirmDelete(); }}
                  />
                  {deleteError && (
                    <div className="alert alert-danger mt-2 mb-0 py-2" style={{ fontSize: '0.82rem' }}>{deleteError}</div>
                  )}
                </>
              )}
            </div>
            <div className="import-tool-modal-header" style={{ borderTop: '1px solid #E8DDD4', borderBottom: 'none', justifyContent: 'flex-end', gap: '0.5rem' }}>
              {deleteStage === 'confirm' ? (
                <>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeDeleteModal} disabled={deleting}>Cancel</button>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => { setDeleteError(null); setDeleteConfirmText(''); setDeleteStage('type'); }}
                  >
                    Continue
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => { setDeleteError(null); setDeleteStage('confirm'); }}
                    disabled={deleting}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={handleConfirmDelete}
                    disabled={!deleteConfirmMatches || deleting}
                  >
                    {deleting
                      ? <><span className="spinner-border spinner-border-sm me-1" />Deleting…</>
                      : `Delete ${selectedCount} item${selectedCount === 1 ? '' : 's'}`}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {historyItem && createPortal(
        <div
          className="import-tool-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) mouseDownOnOverlayRef.current = true;
          }}
          onMouseUp={(e) => {
            if (mouseDownOnOverlayRef.current && e.target === e.currentTarget) {
              closePriceHistory();
            }
            mouseDownOnOverlayRef.current = false;
          }}
        >
          <div
            className="import-tool-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Item price timeline"
            onMouseDown={e => e.stopPropagation()}
            style={{ maxWidth: '960px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="import-tool-modal-header">
              <div>
                <h6 style={{ margin: 0, color: '#3a2e28' }}>Item price timeline</h6>
                {/* Canonical name plus the linked-product count. Naming one
                    vendor here would pick a winner among several. */}
                <div style={{ fontSize: '0.8rem', color: '#6f6259', marginTop: '0.15rem' }}>
                  {historyItem.description || 'Item'}
                  {costSummaryByItemId[historyItem.id]?.isMultiVendor
                    ? ` • ${describePurchaseOptions(costSummaryByItemId[historyItem.id].optionsSummary)}`
                    : historyItem.vendor ? ` • ${historyItem.vendor}` : ''}
                </div>
              </div>
              <button
                type="button"
                className="import-tool-modal-close"
                onClick={closePriceHistory}
                aria-label="Close"
              >×</button>
            </div>
            <div className="import-tool-modal-body" style={{ overflowY: 'auto', flex: 1, padding: '1rem' }}>
              {historyLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : historyError ? (
                <div className="alert alert-danger py-2 mb-0" style={{ fontSize: '0.85rem' }}>
                  {historyError}
                </div>
              ) : (
                <>
                  {/* What the item costs NOW, beside the record of how it got
                      there — so the timeline is read against the cost counts
                      and recipes actually use. */}
                  {costSummaryByItemId[historyItem.id] && (
                    <div style={{ marginBottom: '1rem' }}>
                      <ItemPurchaseSummary
                        summary={costSummaryByItemId[historyItem.id]}
                        headline={false}
                        loading={optionsLoading}
                        onViewOptions={() => {
                          closePriceHistory();
                          setPurchaseOptionsItem(inventoryById[historyItem.id] || historyItem);
                        }}
                      />
                    </div>
                  )}
                  <ItemPriceTimeline
                    item={historyItem}
                    historyRows={historyRows}
                    multiVendor={!!costSummaryByItemId[historyItem.id]?.isMultiVendor}
                  />
                </>
              )}
            </div>
            <div className="import-tool-modal-header" style={{ borderTop: '1px solid #E8DDD4', borderBottom: 'none', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={exportPriceHistoryCsv}
                disabled={historyLoading || !!historyError || historyRows.length === 0}
                title={historyRows.length === 0 ? 'No price changes to export' : 'Download displayed rows as CSV'}
              >
                Export CSV
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closePriceHistory}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Vendor Item Matching stays separate from canonical-item relationships. */}
      {vendorMatchingOpen && createPortal(
        <div
          className="import-tool-modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) mouseDownOnOverlayRef.current = true;
          }}
          onMouseUp={(event) => {
            if (mouseDownOnOverlayRef.current && event.target === event.currentTarget) {
              setVendorMatchingOpen(false);
            }
            mouseDownOnOverlayRef.current = false;
          }}
        >
          <div
            className="import-tool-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Vendor Item Matching"
            onMouseDown={(event) => event.stopPropagation()}
            style={{ maxWidth: '820px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="import-tool-modal-header">
              <div>
                <h6 style={{ margin: 0, color: '#3a2e28' }}>Vendor Item Matching</h6>
                <div style={{ fontSize: '0.8rem', color: '#6f6259', marginTop: '0.15rem' }}>
                  Group vendor SKUs that belong to one canonical inventory item. This does not create product relationships.
                </div>
              </div>
              <button type="button" className="import-tool-modal-close" onClick={() => setVendorMatchingOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="import-tool-modal-body" style={{ overflowY: 'auto', flex: 1, padding: '1rem' }}>
              <CrossReferencesTab
                storeId={currentStore?.id}
                items={inventories}
                showRelationships={false}
                onChanged={() => {
                  if (!currentStore?.id) return;
                  fetchInventories(currentStore.id);
                  refreshPendingVendorMatchCount(currentStore.id);
                  refreshItemRelationships(currentStore.id);
                }}
              />
            </div>
            <div className="import-tool-modal-header" style={{ borderTop: '1px solid #E8DDD4', borderBottom: 'none', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setVendorMatchingOpen(false)}>Close</button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Purchase Options Modal — vendor-specific ways to buy one canonical item */}
      {purchaseOptionsItem && createPortal(
        <div
          className="import-tool-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) mouseDownOnOverlayRef.current = true;
          }}
          onMouseUp={(e) => {
            if (mouseDownOnOverlayRef.current && e.target === e.currentTarget) {
              setPurchaseOptionsItem(null);
            }
            mouseDownOnOverlayRef.current = false;
          }}
        >
          <div
            className="import-tool-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Purchase options"
            onMouseDown={e => e.stopPropagation()}
            style={{ maxWidth: '760px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="import-tool-modal-header">
              <div>
                <h6 style={{ margin: 0, color: '#3a2e28' }}>Vendor Options</h6>
                <div style={{ fontSize: '0.8rem', color: '#6f6259', marginTop: '0.15rem' }}>
                  {purchaseOptionsItem.description || 'Item'}
                  {purchaseOptionsItem.unit ? ` • counted in ${purchaseOptionsItem.unit}` : ''}
                </div>
              </div>
              <button
                type="button"
                className="import-tool-modal-close"
                onClick={() => setPurchaseOptionsItem(null)}
                aria-label="Close"
              >×</button>
            </div>
            {/* The purchase-options content itself, not a description of it —
                the button that opened this modal lands the user on the vendor
                products in one step. */}
            <div className="import-tool-modal-body" style={{ overflowY: 'auto', flex: 1, padding: '1rem' }}>
              <PurchaseOptionsEditor
                storeId={currentStore?.id}
                inventoryItemId={purchaseOptionsItem.id}
                countUnit={purchaseOptionsItem.unit || ''}
                item={purchaseOptionsItem}
                store={storeSettings}
                allowSingleOptionComparisonReview={purchaseOptionsItem.__openComparisonReview === true}
                initialComparisonReviewOpen={purchaseOptionsItem.__openComparisonReview === true}
                comparisonReviewUnit={purchaseOptionsItem.__comparisonReviewUnit || ''}
                onChanged={() => {
                  if (currentStore?.id) loadPurchaseOptions(currentStore.id, inventories);
                }}
              />
              <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #E8DDD4' }}>
                <h6 style={{ margin: '0 0 0.15rem', color: '#3a2e28' }}>Connect &amp; disconnect items</h6>
                <p style={{ fontSize: '0.8rem', color: '#6f6259', margin: '0 0 0.75rem' }}>
                  Combine this item with another catalog item, or split a vendor product
                  off into its own item.
                </p>
                <CanonicalLinkEditor
                  storeId={currentStore?.id}
                  item={purchaseOptionsItem}
                  options={optionsByItemId[purchaseOptionsItem.id] || []}
                  vendorNameById={vendorNameById}
                  onChanged={() => {
                    if (currentStore?.id) fetchInventories(currentStore.id);
                  }}
                />
              </div>
            </div>
            <div className="import-tool-modal-header" style={{ borderTop: '1px solid #E8DDD4', borderBottom: 'none', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPurchaseOptionsItem(null)}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Catalog pill group modal — the full relationship group, same editor as the Related Products tab */}
      {catalogEditingGroup && createPortal(
        <div
          className="import-tool-modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setCatalogGroupModalKey(null);
          }}
        >
          <div
            className="import-tool-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Related products for ${catalogEditingGroup.canonicalName}`}
            onMouseDown={(event) => event.stopPropagation()}
            style={{ maxWidth: 760, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="import-tool-modal-header">
              <div>
                <h6 style={{ margin: 0, color: '#3a2e28' }}>Related products</h6>
                <div style={{ fontSize: '0.8rem', color: '#6f6259', marginTop: '0.15rem' }}>
                  Every vendor product in this group — the full set the pill counts
                </div>
              </div>
              <button
                type="button"
                className="import-tool-modal-close"
                onClick={() => setCatalogGroupModalKey(null)}
                aria-label="Close"
              >×</button>
            </div>
            <div className="import-tool-modal-body" style={{ overflowY: 'auto', flex: 1, padding: '1rem' }}>
              <RelatedProductGroupEditor
                group={catalogEditingGroup}
                factsByOptionId={catalogEditingComparison.entriesByOptionId}
                comparisonUnitLabel={catalogEditingComparison.unitLabel}
                itemRelationships={itemRelationships}
                items={catalogProjectionItems}
                optionsByItemId={optionsByItemId}
                vendorNameById={vendorNameById}
                itemById={catalogGroupItemById}
                storeId={currentStore?.id}
                onEditItem={(item, { reviewComparison = false, comparisonUnit = '' } = {}) => {
                  setCatalogGroupModalKey(null);
                  const resolved = inventoryById[item.id] || item;
                  setPurchaseOptionsItem(reviewComparison
                    ? {
                        ...resolved,
                        __openComparisonReview: true,
                        __comparisonReviewUnit: comparisonUnit,
                      }
                    : resolved);
                }}
                onChanged={async () => {
                  if (!currentStore?.id) return;
                  await Promise.all([
                    refreshItemRelationships(currentStore.id),
                    fetchInventories(currentStore.id),
                  ]);
                }}
                onClose={() => setCatalogGroupModalKey(null)}
              />
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Manual Entry Modal */}
      {isManualEntryOpen && createPortal(
        <div
          className="import-tool-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) mouseDownOnOverlayRef.current = true;
          }}
          onMouseUp={(e) => {
            if (mouseDownOnOverlayRef.current && e.target === e.currentTarget) {
              setIsManualEntryOpen(false);
            }
            mouseDownOnOverlayRef.current = false;
          }}
        >
          <div
            className="import-tool-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Manual item entry"
            onMouseDown={e => e.stopPropagation()}
            style={{ maxWidth: '860px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="import-tool-modal-header">
              <h6 style={{ margin: 0 }}>📝 Manual Entry — Add Items</h6>
              <button
                type="button"
                className="import-tool-modal-close"
                onClick={() => setIsManualEntryOpen(false)}
                aria-label="Close"
              >×</button>
            </div>
            <div className="import-tool-modal-body" style={{ overflowY: 'auto', flex: 1, padding: '1rem' }}>
              <p className="text-muted" style={{ fontSize: '0.82rem', marginBottom: '0.75rem' }}>
                Fill in each row. <strong>Inventory item name, Category, Unit and Location</strong> are required.
                <br />
                Use the name your team should see while counting, such as Whole Milk.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr style={{ background: '#F5F3F0', borderBottom: '2px solid #D4C4B0' }}>
                      <th style={{ padding: '6px 4px', width: '28px', color: '#9A8B82', fontWeight: 600 }}>#</th>
                      {MANUAL_ENTRY_COLUMNS.map(col => (
                        <th key={col.key} style={{ padding: '6px 8px', textAlign: 'left', color: '#5A4A42', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {col.label}{col.required && <span style={{ color: '#dc3545' }}> *</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {manualRows.map((row, rowIdx) => (
                      <tr key={rowIdx} style={{ borderBottom: '1px solid #EDE8E3' }}>
                        <td style={{ padding: '4px', color: '#B0A09A', textAlign: 'center', fontSize: '0.72rem' }}>{rowIdx + 1}</td>
                        {MANUAL_ENTRY_COLUMNS.map(col => (
                          <td key={col.key} style={{ padding: '3px 4px' }}>
                            <input
                              type={col.numeric ? 'number' : 'text'}
                              inputMode={col.numeric ? 'decimal' : undefined}
                              step={col.numeric ? '0.01' : undefined}
                              min={col.numeric ? '0' : undefined}
                              value={row[col.key]}
                              onChange={e => updateManualRow(rowIdx, col.key, e.target.value)}
                              placeholder={col.placeholder}
                              className="form-control form-control-sm"
                              style={{
                                fontSize: '0.8rem',
                                borderColor:
                                  (col.required && !String(row[col.key] ?? '').trim() && manualSaveError) ||
                                  (col.numeric && manualPriceErrorCells?.has(`${rowIdx}:${col.key}`))
                                    ? '#dc3545'
                                    : undefined,
                                minWidth: col.key === 'description' ? '160px' : (col.numeric ? '80px' : '90px')
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="d-flex gap-2 mt-2">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addManualRows(1)}>+ 1 row</button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addManualRows(5)}>+ 5 rows</button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addManualRows(10)}>+ 10 rows</button>
              </div>
              {manualSaveError && (
                <div className="alert alert-danger mt-2 mb-0 py-2" style={{ fontSize: '0.82rem' }}>{manualSaveError}</div>
              )}
            </div>
            <div className="import-tool-modal-header" style={{ borderTop: '1px solid #E8DDD4', borderBottom: 'none', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setIsManualEntryOpen(false)} disabled={manualSaving}>Cancel</button>
              <button type="button" className="btn btn-sm btn-primary" onClick={handleManualEntrySave} disabled={manualSaving}>
                {manualSaving ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</> : 'Save Items'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Cross-reference review — a sequential LinkItemDialog queue shown when
          a manual-entry row looks like an item that already exists. */}
      {linkReview && createPortal(
        <LinkItemDialog
          candidates={linkReview.enrichedCandidates[linkReview.queueIdxs[linkReview.cursor]] || []}
          newItemDraft={(() => {
            const ctx = linkReview.contexts[linkReview.queueIdxs[linkReview.cursor]];
            return ctx ? {
              name: ctx.row.description.trim(),
              vendor: ctx.row.vendor.trim(),
              unit: ctx.row.unit.trim(),
              packSize: normalizeText(ctx.row.pack_size),
            } : {};
          })()}
          queuePosition={{ index: linkReview.cursor + 1, total: linkReview.queueIdxs.length }}
          onLink={(candidate) => resolveLinkReviewRow({ type: 'link', itemId: candidate.itemId })}
          onCreateSeparate={() => resolveLinkReviewRow({ type: 'separate' })}
          onCancel={() => resolveLinkReviewRow({ type: 'abort' })}
        />,
        document.body
      )}
    </div>
  );
};

export default InventoryManager;
